import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit, Trash2, Search, ChevronDown, ChevronRight, RefreshCw, Copy, X, CheckCircle, Trash, Download, AlertTriangle, Eye, Printer, Calendar, Moon, Users } from 'lucide-react';
import AutoCreerOptionsCSModal from '../GestionOptions/AutoCreerOptionsCSModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';

// APIs
import {
  useGetMatieresQuery,
  useCreateMatiereMutation,
  useUpdateMatiereMutation,
  useDeleteMatiereMutation,
  useBatchCreateMatieresMutation,
  useToggleActifMatiereMutation,
  useToggleActifMultipleMutation,
  useCopierMatieresVersOptionMutation,
  useGetOrphelinesQuery,
  useRattacherMatieresMutation,
  useNettoyerOrphelinesMutation,
  useGetSujetsAImprimerQuery,
  Matiere,
} from '../../services/matieresApi';
import {
  useGetUEsQuery,
  useCreateUEMutation,
  useUpdateUEMutation,
  useDeleteUEMutation,
  useCopierUEsVersOptionMutation,
  useApercuNettoyageMutation,
  useNettoyageMutation,
  BackupUE,
  NettoyageFiltres,
} from '../../services/uesApi';
import { useGetOptionsQuery, useGetPolesQuery, useGetParcoursListQuery, useGetClassesQuery, useDeleteOptionMutation } from '../../services/polesOptionsApi';
import {
  useGetFormateursQuery,
  useGetAttributionsAnneeQuery,
  useAttribuerMatiereMutation,
  useGetSuggestionsAffectationQuery,
  useAppliquerSuggestionsMutation,
  useGetPairesSimilairesQuery,
  useCopierAttributionsMutation,
} from '../../services/formateursApi';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';
import { useGetChoixFormulaireQuery } from '../../services/etudiantsApi';

// Niveaux disponibles (statiques car lies au systeme LMD)
const NIVEAUX = [
  { value: 'L1', label: 'Licence 1' },
  { value: 'L2', label: 'Licence 2' },
  { value: 'L3', label: 'Licence 3' },
  { value: 'M1', label: 'Master 1' },
  { value: 'M2', label: 'Master 2' },
];

const getSemestresForNiveau = (niveau: string): { value: number; label: string }[] => {
  switch (niveau) {
    case 'L1': return [{ value: 1, label: 'S1' }, { value: 2, label: 'S2' }];
    case 'L2': return [{ value: 3, label: 'S3' }, { value: 4, label: 'S4' }];
    case 'L3': return [{ value: 5, label: 'S5' }, { value: 6, label: 'S6' }];
    case 'M1': return [{ value: 7, label: 'S7' }, { value: 8, label: 'S8' }];
    case 'M2': return [{ value: 9, label: 'S9' }, { value: 10, label: 'S10' }];
    default: return [];
  }
};

// Composant dropdown formateur avec barre de recherche
const FormateurSearchSelect: React.FC<{
  formateurs: Array<{ id: number; nom: string; prenoms: string }>;
  value: number | undefined;
  onChange: (id: number) => void;
}> = ({ formateurs, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return formateurs || [];
    const q = search.toLowerCase();
    return (formateurs || []).filter(
      (f) => f.nom.toLowerCase().includes(q) || f.prenoms.toLowerCase().includes(q)
    );
  }, [formateurs, search]);

  const selected = formateurs?.find((f) => f.id === value);

  return (
    <div ref={ref} className="relative w-52">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left truncate flex items-center justify-between"
      >
        <span className="truncate">{selected ? `${selected.nom} ${selected.prenoms}` : 'Choisir formateur...'}</span>
        <ChevronDown size={12} className="ml-1 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 flex flex-col">
          <div className="p-1.5 border-b border-gray-200 dark:border-gray-700">
            <input
              autoFocus
              type="text"
              placeholder="Rechercher un formateur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            <button
              type="button"
              onClick={() => { onChange(0); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              — Aucun —
            </button>
            {filtered.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { onChange(f.id); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-900/30 ${
                  value === f.id ? 'bg-blue-50 dark:bg-blue-900/30 font-medium text-blue-700 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'
                }`}
              >
                {f.nom} {f.prenoms}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400 italic">Aucun résultat</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const GestionMatieres: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'matieres' | 'ues' | 'diagnostic' | 'recapitulatif' | 'desactivees'>('matieres');
  const [recapView, setRecapView] = useState<'parcours' | 'planning'>('parcours');
  const [planningNiveau, setPlanningNiveau] = useState('L1');
  const [planningSemestre, setPlanningSemestre] = useState(1);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNiveau, setFilterNiveau] = useState('');
  const [filterOption, setFilterOption] = useState('');
  const [filterSemestre, setFilterSemestre] = useState('');
  const [filterParcours, setFilterParcours] = useState<number | ''>('');
  const [filterPole, setFilterPole] = useState<number | ''>('');
  // FI = Formation Initiale, CS = Cours du Soir. Détection via suffixe -CS du code option/matière.
  const [filterFormation, setFilterFormation] = useState<'' | 'FI' | 'CS'>('');

  // Modal "Auto-créer options CS" — détecte les étudiants type_formation=CS
  // qui n'ont pas encore d'option -CS dédiée et duplique les matières/UE depuis FI.
  const [showAutoCSModal, setShowAutoCSModal] = useState(false);

  // Modal d'impression
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printPole, setPrintPole] = useState<number | ''>('');
  const [printNiveau, setPrintNiveau] = useState('');
  const [printOption, setPrintOption] = useState('');

  // Sélection multiple matières
  const [selectedMatieres, setSelectedMatieres] = useState<Set<number>>(new Set());
  const [deletingBatch, setDeletingBatch] = useState(false);

  // Modals
  const [showMatiereModal, setShowMatiereModal] = useState(false);
  const [showUEModal, setShowUEModal] = useState(false);
  const [editingMatiere, setEditingMatiere] = useState<Matiere | null>(null);
  const [editingUE, setEditingUE] = useState<any>(null);

  // Copie modal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyForm, setCopyForm] = useState({
    option_source: '',
    options_cibles: [] as string[],
    niveau: 'L1',
  });

  // Copie matières modal
  const [showCopyMatieresModal, setShowCopyMatieresModal] = useState(false);
  const [copyMatieresOptionsCibles, setCopyMatieresOptionsCibles] = useState<string[]>([]);

  // Nettoyage modal
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupStep, setCleanupStep] = useState<1 | 2>(1);
  const [cleanupNiveau, setCleanupNiveau] = useState('L1');
  const [cleanupScope, setCleanupScope] = useState<'option' | 'options' | 'parcours' | 'pole' | 'tout'>('tout');
  const [cleanupOption, setCleanupOption] = useState('');
  const [cleanupSelectedOptions, setCleanupSelectedOptions] = useState<string[]>([]);
  const [cleanupPole, setCleanupPole] = useState<number | ''>('');
  const [cleanupParcours, setCleanupParcours] = useState<number | ''>('');
  const [cleanupApercu, setCleanupApercu] = useState<{ nb_ues: number; nb_matieres: number; nb_orphelines?: number; backup: BackupUE[]; dependances?: Record<string, number> } | null>(null);

  // UE Expansion
  const [expandedUEs, setExpandedUEs] = useState<Set<number>>(new Set());

  // Matières groupées expansion (par classe code)
  const [expandedMatiereGroups, setExpandedMatiereGroups] = useState<Set<string>>(new Set());

  // Batch modal
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchExpandedGroups, setBatchExpandedGroups] = useState<string[]>([]);
  // Filtre type_formation dans le batch : 'FI', 'CS' ou '' (les deux).
  // Sépare visuellement + empêche les sélections croisées (matières CS ajoutées à des options FI).
  const [batchFormationFilter, setBatchFormationFilter] = useState<'' | 'FI' | 'CS'>('');
  const [batchForm, setBatchForm] = useState({
    noms: '',
    niveau: 'L1',
    semestre: 1,
    selectedOptionIds: [] as number[],
  });

  // Forms
  const [matiereForm, setMatiereForm] = useState({
    code_matiere: '',
    nom_matiere: '',
    ue: '',
    niveau: 'L1',
    semestre: 1,
    credits: 2,
    coefficient_devoir: 0.4,
    coefficient_examen: 0.6,
  });
  // Type formation du formulaire d'ajout (filtre les UE proposées)
  const [matiereFormFormation, setMatiereFormFormation] = useState<'' | 'FI' | 'CS'>('');
  // Option choisie dans le formulaire d'ajout (filtre les UE proposées)
  const [matiereFormOption, setMatiereFormOption] = useState<string>('');

  const [ueForm, setUeForm] = useState({
    code_ue: '',
    nom_ue: '',
    niveau: 'L1',
    semestre: 1,
    option: '',
  });

  // Messages
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Rattachement orphelines
  const [showRattachModal, setShowRattachModal] = useState(false);
  const [rattachOption, setRattachOption] = useState('');
  const [rattachSelectedIds, setRattachSelectedIds] = useState<Set<number>>(new Set());

  // Nettoyage orphelines
  const [showNettoyerModal, setShowNettoyerModal] = useState(false);
  const [nettoyerAnalyse, setNettoyerAnalyse] = useState<any>(null);

  // Sujets à imprimer
  const [showSujetsModal, setShowSujetsModal] = useState(false);
  const [sujetsNiveau, setSujetsNiveau] = useState('L1');
  const [sujetsOption, setSujetsOption] = useState('');
  const [sujetsSemestre, setSujetsSemestre] = useState('');

  // Diagnostic: attribution formateur inline
  const [diagFormateurSelections, setDiagFormateurSelections] = useState<Record<number, number>>({});
  const [diagExpandedClasses, setDiagExpandedClasses] = useState<Set<string>>(new Set());

  // Queries
  // Fix 2026-09-09 : `actif=all` pour charger aussi les désactivées
  // (onglet dédié "Désactivées"). Les actives restent affichées par défaut.
  const { data: matieresAll, isLoading: matieresLoading, refetch: refetchMatieres } =
    useGetMatieresQuery({ actif: 'all' });
  const matieres = useMemo(
    () => (matieresAll || []).filter((m: any) => m.actif),
    [matieresAll],
  );
  const matieresDesactivees = useMemo(
    () => (matieresAll || []).filter((m: any) => !m.actif),
    [matieresAll],
  );
  const { data: ues, isLoading: uesLoading, refetch: refetchUEs } = useGetUEsQuery();
  const { data: options } = useGetOptionsQuery({ actif: true });
  const { data: poles } = useGetPolesQuery({ actif: true });
  const { data: parcoursList } = useGetParcoursListQuery({ actif: true });
  const { data: classes } = useGetClassesQuery({ actif: true });
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const { data: formateurs } = useGetFormateursQuery({ actif: true });
  const { data: choixFormulaire } = useGetChoixFormulaireQuery({ exclure_type_formation: 'CS' });
  const { data: attributionsAnnee, refetch: refetchAttributions } = useGetAttributionsAnneeQuery(
    { annee_academique: anneeActive?.libelle ?? '' },
    { skip: !anneeActive }
  );

  // Mutations
  const [createMatiere, { isLoading: createMatiereLoading }] = useCreateMatiereMutation();
  const [updateMatiere, { isLoading: updateMatiereLoading }] = useUpdateMatiereMutation();
  const [deleteMatiere] = useDeleteMatiereMutation();
  const [createUE, { isLoading: createUELoading }] = useCreateUEMutation();
  const [updateUE, { isLoading: updateUELoading }] = useUpdateUEMutation();
  const [deleteUE] = useDeleteUEMutation();
  const [batchCreate, { isLoading: batchCreateLoading }] = useBatchCreateMatieresMutation();
  const [copierUEs, { isLoading: copierLoading }] = useCopierUEsVersOptionMutation();
  const [copierMatieres, { isLoading: copierMatieresLoading }] = useCopierMatieresVersOptionMutation();
  const [apercuNettoyage, { isLoading: apercuLoading }] = useApercuNettoyageMutation();
  const [executerNettoyage, { isLoading: nettoyageLoading }] = useNettoyageMutation();
  const [attribuerMatiere] = useAttribuerMatiereMutation();
  const [deleteOption] = useDeleteOptionMutation();
  const [toggleActifMatiere] = useToggleActifMatiereMutation();
  const { data: orphelinesData, refetch: refetchOrphelines } = useGetOrphelinesQuery();
  const [rattacherMatieres, { isLoading: rattachLoading }] = useRattacherMatieresMutation();
  const [nettoyerOrphelines, { isLoading: nettoyerLoading }] = useNettoyerOrphelinesMutation();
  const { data: sujetsData, isFetching: sujetsFetching } = useGetSujetsAImprimerQuery(
    { niveau: sujetsNiveau, option: sujetsOption || undefined, semestre: sujetsSemestre || undefined },
    { skip: !showSujetsModal }
  );
  const [toggleActifMultiple] = useToggleActifMultipleMutation();
  const [appliquerSuggestions, { isLoading: appliquantSuggestions }] = useAppliquerSuggestionsMutation();
  const { data: suggestionsData, refetch: refetchSuggestions } = useGetSuggestionsAffectationQuery(
    { annee_academique: anneeActive?.libelle ?? '' },
    { skip: !anneeActive }
  );

  // Filtered data
  // Options filtrées par pôle ET parcours (cascade) + type formation (FI/CS)
  const optionsFiltered = useMemo(() => {
    if (!options) return [];
    let result = options;
    if (filterPole) result = result.filter((o) => o.pole === filterPole);
    if (filterParcours) result = result.filter((o) => o.parcours === filterParcours);
    if (filterFormation === 'CS') result = result.filter((o) => (o.code || '').endsWith('-CS'));
    if (filterFormation === 'FI') result = result.filter((o) => !(o.code || '').endsWith('-CS'));
    return result;
  }, [options, filterPole, filterParcours, filterFormation]);

  // Nombre total d'options CS existantes (peu importe les filtres) — pour la bannière CS
  const totalOptionsCS = useMemo(
    () => (options || []).filter((o: any) => (o.code || '').endsWith('-CS')).length,
    [options],
  );

  // Noms d'options autorisées par les filtres hiérarchiques (pôle → parcours → option → formation)
  const optionNomsAutorisees = useMemo(() => {
    if (!filterPole && !filterParcours && !filterFormation) return null;
    return new Set(optionsFiltered.map((o) => o.nom));
  }, [filterPole, filterParcours, filterFormation, optionsFiltered]);

  // Index options par nom pour résolution rapide du code (utilisé par isMatiereCS)
  const optionsByNom = useMemo(() => {
    const map: Record<string, any> = {};
    (options || []).forEach((o: any) => { if (o.nom) map[o.nom] = o; });
    return map;
  }, [options]);

  // Helper : matière est en CS ? (suffixe -CS sur code matière OU sur code option)
  const isMatiereCS = useCallback((m: any) => {
    if ((m.code_matiere || '').endsWith('-CS')) return true;
    const opt = m.option ? optionsByNom[m.option] : null;
    if (opt && (opt.code || '').endsWith('-CS')) return true;
    return false;
  }, [optionsByNom]);

  const filteredMatieres = useMemo(() => {
    if (!matieres) return [];
    return matieres.filter((m) => {
      const matchSearch =
        !searchTerm ||
        m.code_matiere.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.nom_matiere.toLowerCase().includes(searchTerm.toLowerCase());
      const matchNiveau = !filterNiveau || m.niveau === filterNiveau;
      const matchOption = !filterOption || m.option === filterOption;
      const matchSemestre = !filterSemestre || m.semestre?.toString() === filterSemestre;
      const matchHierarchy = !optionNomsAutorisees || (m.option ? optionNomsAutorisees.has(m.option) : false);
      const matchFormation = !filterFormation
        || (filterFormation === 'CS' && isMatiereCS(m))
        || (filterFormation === 'FI' && !isMatiereCS(m));
      return matchSearch && matchNiveau && matchOption && matchSemestre && matchHierarchy && matchFormation;
    });
  }, [matieres, searchTerm, filterNiveau, filterOption, filterSemestre, optionNomsAutorisees, filterFormation, isMatiereCS]);

  const filteredUEs = useMemo(() => {
    if (!ues) return [];
    return ues.filter((ue) => {
      const matchSearch =
        !searchTerm ||
        ue.code_ue.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ue.nom_ue.toLowerCase().includes(searchTerm.toLowerCase());
      const matchNiveau = !filterNiveau || ue.niveau === filterNiveau;
      const matchOption = !filterOption || ue.option === filterOption;
      const matchSemestre = !filterSemestre || ue.semestre?.toString() === filterSemestre;
      const matchHierarchy = !optionNomsAutorisees || (ue.option ? optionNomsAutorisees.has(ue.option) : false);
      // Filtre formation : UE est CS si son code finit par -CS ou si option associée est CS
      const ueIsCS = (ue.code_ue || '').endsWith('-CS')
        || (ue.option && optionsByNom[ue.option]?.code?.endsWith('-CS'));
      const matchFormation = !filterFormation
        || (filterFormation === 'CS' && ueIsCS)
        || (filterFormation === 'FI' && !ueIsCS);
      return matchSearch && matchNiveau && matchOption && matchSemestre && matchHierarchy && matchFormation;
    });
  }, [ues, searchTerm, filterNiveau, filterOption, filterSemestre, optionNomsAutorisees, filterFormation, optionsByNom]);

  // Diagnostic : matières sans formateur + options sans matière
  // On applique les MÊMES filtres que l'onglet Matières (search/niveau/option/
  // semestre/hiérarchie) pour permettre d'attribuer ciblé par semestre p.ex.
  const matieresSansFormateur = useMemo(() => {
    if (!matieres || !attributionsAnnee) return [];
    return matieres.filter((m) => {
      if (attributionsAnnee[String(m.id)]) return false; // a déjà un formateur
      const matchSearch =
        !searchTerm ||
        m.code_matiere.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.nom_matiere.toLowerCase().includes(searchTerm.toLowerCase());
      const matchNiveau = !filterNiveau || m.niveau === filterNiveau;
      const matchOption = !filterOption || m.option === filterOption;
      const matchSemestre = !filterSemestre || m.semestre?.toString() === filterSemestre;
      const matchHierarchy = !optionNomsAutorisees || (m.option ? optionNomsAutorisees.has(m.option) : false);
      return matchSearch && matchNiveau && matchOption && matchSemestre && matchHierarchy;
    });
  }, [matieres, attributionsAnnee, searchTerm, filterNiveau, filterOption, filterSemestre, optionNomsAutorisees]);

  // Map option nom → code pour badges classe
  const optionNomToCode = useMemo(() => {
    const map = new Map<string, string>();
    options?.forEach((o) => { if (o.nom && o.code) map.set(o.nom, o.code); });
    return map;
  }, [options]);

  // Map (niveau + option_nom) → code classe réel (ex: "L1MI", "L2BFA")
  const classeCodeMap = useMemo(() => {
    const map = new Map<string, string>();
    classes?.forEach((c: any) => {
      const optNom = c.option_nom || '';
      const niv = c.niveau || '';
      if (niv && optNom) map.set(`${niv}|${optNom}`, c.code);
    });
    return map;
  }, [classes]);

  const niveauxOrdonnes = ['L1', 'L2', 'L3', 'M1', 'M2'];

  // Résoudre le code classe d'une matière (priorité: table Classe, puis reconstruction)
  const resolveClasseCode = useCallback((m: { niveau?: string | null; option?: string | null }) => {
    const niv = m.niveau || '';
    const opt = m.option || '';
    // 1) Chercher dans la table Classe
    if (niv && opt) {
      const real = classeCodeMap.get(`${niv}|${opt}`);
      if (real) return real;
    }
    // 2) Tronc Commun
    if (opt === 'Tronc Commun') return `${niv}TC`;
    // 3) Reconstruction via code option
    const optCode = opt ? optionNomToCode.get(opt) : null;
    if (optCode) return `${niv}${optCode}`;
    // 4) Fallback
    return opt ? `${niv}-${opt}` : niv || 'Autre';
  }, [classeCodeMap, optionNomToCode]);

  // Matières filtrées groupées par classe (pour l'onglet Matières)
  const matieresGroupees = useMemo(() => {
    const groups: Record<string, { classeCode: string; optionNom: string; niveau: string; matieres: Matiere[] }> = {};
    filteredMatieres.forEach((m) => {
      const classeCode = resolveClasseCode(m);
      const optionNom = m.option || 'Sans option';
      if (!groups[classeCode]) groups[classeCode] = { classeCode, optionNom, niveau: m.niveau || '', matieres: [] };
      groups[classeCode].matieres.push(m);
    });
    return groups;
  }, [filteredMatieres, resolveClasseCode]);

  const matieresGroupKeys = useMemo(() => {
    return Object.keys(matieresGroupees).sort((a, b) => {
      const nA = niveauxOrdonnes.indexOf(matieresGroupees[a].niveau);
      const nB = niveauxOrdonnes.indexOf(matieresGroupees[b].niveau);
      if (nA !== nB) return nA - nB;
      return a.localeCompare(b);
    });
  }, [matieresGroupees]);

  // Auto-déplier : si 1 seul groupe ou si une option spécifique est sélectionnée
  useEffect(() => {
    if (matieresGroupKeys.length <= 3 || filterOption) {
      setExpandedMatiereGroups(new Set(matieresGroupKeys));
    } else {
      setExpandedMatiereGroups(new Set());
    }
  }, [matieresGroupKeys, filterOption]);

  // Index nom+niveau → option (pour résoudre les matières orphelines sans UE)
  const matiereOptionIndex = useMemo(() => {
    const idx = new Map<string, string>();
    matieres?.forEach((m) => {
      if (m.option && m.niveau && m.nom_matiere) {
        const key = `${m.niveau}|${m.nom_matiere}`;
        if (!idx.has(key)) idx.set(key, m.option);
      }
    });
    return idx;
  }, [matieres]);

  // Matières sans formateur groupées par classe
  const diagMatieresParClasse = useMemo(() => {
    const groups: Record<string, { classeCode: string; niveau: string; matieres: typeof matieresSansFormateur }> = {};
    matieresSansFormateur.forEach((m) => {
      // Si la matière n'a pas d'option, chercher un doublon qui en a une
      let mResolved = m;
      if (!m.option && m.niveau && m.nom_matiere) {
        const optFromDup = matiereOptionIndex.get(`${m.niveau}|${m.nom_matiere}`);
        if (optFromDup) {
          mResolved = { ...m, option: optFromDup };
        }
      }
      const classeCode = resolveClasseCode(mResolved);
      if (!groups[classeCode]) groups[classeCode] = { classeCode, niveau: m.niveau || '', matieres: [] };
      groups[classeCode].matieres.push(m);
    });
    return groups;
  }, [matieresSansFormateur, resolveClasseCode, matiereOptionIndex]);

  const diagClassesOrdonnees = useMemo(() => {
    return Object.keys(diagMatieresParClasse).sort((a, b) => {
      const nA = niveauxOrdonnes.indexOf(diagMatieresParClasse[a].niveau);
      const nB = niveauxOrdonnes.indexOf(diagMatieresParClasse[b].niveau);
      if (nA !== nB) return nA - nB;
      return a.localeCompare(b);
    });
  }, [diagMatieresParClasse]);

  // ─── Matières AVEC formateur (mêmes filtres) — pour vue attribuée éditable ─
  const matieresAvecFormateur = useMemo(() => {
    if (!matieres || !attributionsAnnee) return [];
    return matieres.filter((m) => {
      const attr = attributionsAnnee[String(m.id)];
      if (!attr) return false;
      const matchSearch =
        !searchTerm ||
        m.code_matiere.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.nom_matiere.toLowerCase().includes(searchTerm.toLowerCase());
      const matchNiveau = !filterNiveau || m.niveau === filterNiveau;
      const matchOption = !filterOption || m.option === filterOption;
      const matchSemestre = !filterSemestre || m.semestre?.toString() === filterSemestre;
      const matchHierarchy = !optionNomsAutorisees || (m.option ? optionNomsAutorisees.has(m.option) : false);
      return matchSearch && matchNiveau && matchOption && matchSemestre && matchHierarchy;
    });
  }, [matieres, attributionsAnnee, searchTerm, filterNiveau, filterOption, filterSemestre, optionNomsAutorisees]);

  const diagAttribueesParClasse = useMemo(() => {
    const groups: Record<string, { classeCode: string; niveau: string; matieres: typeof matieresAvecFormateur }> = {};
    matieresAvecFormateur.forEach((m) => {
      let mResolved = m;
      if (!m.option && m.niveau && m.nom_matiere) {
        const optFromDup = matiereOptionIndex.get(`${m.niveau}|${m.nom_matiere}`);
        if (optFromDup) mResolved = { ...m, option: optFromDup };
      }
      const classeCode = resolveClasseCode(mResolved);
      if (!groups[classeCode]) groups[classeCode] = { classeCode, niveau: m.niveau || '', matieres: [] };
      groups[classeCode].matieres.push(m);
    });
    return groups;
  }, [matieresAvecFormateur, resolveClasseCode, matiereOptionIndex]);

  const diagAttribueesClassesOrdonnees = useMemo(() => {
    return Object.keys(diagAttribueesParClasse).sort((a, b) => {
      const nA = niveauxOrdonnes.indexOf(diagAttribueesParClasse[a].niveau);
      const nB = niveauxOrdonnes.indexOf(diagAttribueesParClasse[b].niveau);
      if (nA !== nB) return nA - nB;
      return a.localeCompare(b);
    });
  }, [diagAttribueesParClasse]);

  // État UI dédié à la section attribuées : classes dépliées + sélections changement
  const [diagAttribueesExpanded, setDiagAttribueesExpanded] = useState<Set<string>>(new Set());
  const [diagChangementSelections, setDiagChangementSelections] = useState<Record<number, number>>({});
  const [diagEditionMatiereId, setDiagEditionMatiereId] = useState<number | null>(null);

  const handleChangerFormateurDiag = async (matiereId: number) => {
    const nouveauFormateurId = diagChangementSelections[matiereId];
    if (!nouveauFormateurId || !anneeActive) return;
    const attr = attributionsAnnee?.[String(matiereId)];
    const ancien = attr?.formateur_nom || 'formateur actuel';
    if (!window.confirm(`Remplacer ${ancien} par le nouveau formateur pour cette matière ?`)) return;
    try {
      const result = await attribuerMatiere({
        formateur_id: nouveauFormateurId,
        matiere_id: matiereId,
        annee_academique: anneeActive.libelle,
        force: true,
      }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setDiagChangementSelections((prev) => { const next = { ...prev }; delete next[matiereId]; return next; });
      setDiagEditionMatiereId(null);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.data?.message || err?.data?.error || 'Erreur lors du changement' });
    }
  };

  const optionsSansMatiere = useMemo(() => {
    if (!options || !matieres) return [];
    // Normaliser pour comparer : lowercase + trim
    const optionsAvecMatiere = new Set(
      matieres.map((m) => (m.option || '').toLowerCase().trim()).filter(Boolean)
    );
    // On part de optionsFiltered (qui applique le filtre pôle/parcours) plutôt
    // que de l'ensemble des options, pour respecter le filtre hiérarchique
    return optionsFiltered.filter((o: any) => {
      if (optionsAvecMatiere.has(o.nom.toLowerCase().trim())) return false;
      // Si un filtre niveau est actif, on ne peut pas filtrer ici (Option n'a
      // pas de niveau) — on laisse passer
      if (filterOption && o.nom !== filterOption) return false;
      return true;
    });
  }, [optionsFiltered, matieres, filterOption]);

  const handleAssignerFormateurDiag = async (matiereId: number) => {
    const formateurId = diagFormateurSelections[matiereId];
    if (!formateurId || !anneeActive) return;
    try {
      const result = await attribuerMatiere({
        formateur_id: formateurId,
        matiere_id: matiereId,
        annee_academique: anneeActive.libelle,
      }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setDiagFormateurSelections((prev) => { const next = { ...prev }; delete next[matiereId]; return next; });
    } catch (err: any) {
      const data = err?.data;
      if (data?.conflit) {
        setMessage({ type: 'error', text: `Déjà attribuée à ${data.formateur_nom}` });
      } else {
        setMessage({ type: 'error', text: data?.error || data?.message || 'Erreur lors de l\'attribution' });
      }
    }
  };

  // UEs pour le formulaire de matiere — filtrées par type formation + option choisie
  const uesForMatiereForm = useMemo(() => {
    if (!ues) return [];
    let res: any[] = ues as any[];
    if (matiereFormFormation) {
      res = res.filter((ue: any) => {
        const codeCS = (ue.code_ue || '').endsWith('-CS')
          || (ue.option && optionsByNom[ue.option]?.code?.endsWith('-CS'));
        return matiereFormFormation === 'CS' ? codeCS : !codeCS;
      });
    }
    if (matiereFormOption) {
      res = res.filter((ue: any) => ue.option === matiereFormOption);
    }
    return res;
  }, [ues, matiereFormFormation, matiereFormOption, optionsByNom]);

  // Options pour le dropdown du formulaire — filtrées par type formation.
  // ATTENTION : Redux/RTK Query gèle les arrays. On copie avant .sort() qui mute.
  const optionsForMatiereForm = useMemo(() => {
    if (!options) return [];
    let res: any[] = [...(options as any[])];
    if (matiereFormFormation === 'CS') {
      res = res.filter((o: any) => (o.code || '').endsWith('-CS'));
    } else if (matiereFormFormation === 'FI') {
      res = res.filter((o: any) => !(o.code || '').endsWith('-CS'));
    }
    return res.sort((a: any, b: any) => (a.nom || '').localeCompare(b.nom || ''));
  }, [options, matiereFormFormation]);

  // Handlers
  const handleRefresh = () => {
    refetchMatieres();
    refetchUEs();
  };

  const resetMatiereForm = () => {
    setMatiereForm({
      code_matiere: '',
      nom_matiere: '',
      ue: '',
      niveau: 'L1',
      semestre: 1,
      credits: 2,
      coefficient_devoir: 0.4,
      coefficient_examen: 0.6,
    });
    setMatiereFormFormation('');
    setMatiereFormOption('');
    setEditingMatiere(null);
  };

  const resetUEForm = () => {
    setUeForm({
      code_ue: '',
      nom_ue: '',
      niveau: 'L1',
      semestre: 1,
      option: '',
    });
    setEditingUE(null);
  };

  const handleCreateOrUpdateMatiere = async () => {
    if (!matiereForm.nom_matiere) {
      setMessage({ type: 'error', text: 'Veuillez remplir le nom de la matière' });
      return;
    }

    try {
      // Si UE sélectionnée, dériver niveau/semestre de l'UE
      const selectedUE = matiereForm.ue ? ues?.find((u) => u.id.toString() === matiereForm.ue) : null;

      const matiereData: Record<string, any> = {
        nom_matiere: matiereForm.nom_matiere,
        credits: matiereForm.credits,
        coefficient_devoir: matiereForm.coefficient_devoir,
        coefficient_examen: matiereForm.coefficient_examen,
      };

      // Code matière (vide = auto-généré par le backend)
      if (matiereForm.code_matiere) {
        matiereData.code_matiere = matiereForm.code_matiere;
      }

      if (selectedUE) {
        matiereData.ue = parseInt(matiereForm.ue);
        matiereData.niveau = selectedUE.niveau;
        matiereData.semestre = selectedUE.semestre;
      } else {
        matiereData.ue = null;
        matiereData.niveau = matiereForm.niveau;
        matiereData.semestre = matiereForm.semestre;
      }

      if (editingMatiere) {
        await updateMatiere({ id: editingMatiere.id, matiere: matiereData }).unwrap();
        setMessage({ type: 'success', text: 'Matière mise à jour avec succès' });
      } else {
        await createMatiere(matiereData).unwrap();
        setMessage({ type: 'success', text: 'Matière créée avec succès' });
      }

      setShowMatiereModal(false);
      resetMatiereForm();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.detail || error?.data?.code_matiere?.[0] || 'Erreur lors de la sauvegarde' });
    }
  };

  const handleEditMatiere = (matiere: Matiere) => {
    setEditingMatiere(matiere);
    setMatiereForm({
      code_matiere: matiere.code_matiere,
      nom_matiere: matiere.nom_matiere,
      ue: matiere.ue?.toString() || '',
      niveau: matiere.niveau || 'L1',
      semestre: matiere.semestre || 1,
      credits: matiere.credits || 2,
      coefficient_devoir: matiere.coefficient_devoir || 0.4,
      coefficient_examen: matiere.coefficient_examen || 0.6,
    });
    setShowMatiereModal(true);
  };

  const handleDeleteMatiere = async (id: number) => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer cette matiere ?')) return;
    try {
      await deleteMatiere(id).unwrap();
      setMessage({ type: 'success', text: 'Matiere supprimee avec succes' });
      setSelectedMatieres((prev) => { const next = new Set(prev); next.delete(id); return next; });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const handleToggleSelectMatiere = (id: number) => {
    setSelectedMatieres((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllMatieres = () => {
    if (selectedMatieres.size === filteredMatieres.length) {
      setSelectedMatieres(new Set());
    } else {
      setSelectedMatieres(new Set(filteredMatieres.map((m) => m.id)));
    }
  };

  const handleDeleteSelectedMatieres = async () => {
    if (selectedMatieres.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedMatieres.size} matière(s) sélectionnée(s) ?`)) return;
    setDeletingBatch(true);
    let ok = 0;
    let errors = 0;
    let premierMotif = '';
    for (const id of Array.from(selectedMatieres)) {
      try {
        await deleteMatiere(id).unwrap();
        ok++;
      } catch (err: any) {
        errors++;
        if (!premierMotif) premierMotif = err?.data?.detail || '';
      }
    }
    setSelectedMatieres(new Set());
    setDeletingBatch(false);
    if (errors > 0) {
      setMessage({
        type: 'error',
        text: `${ok} supprimée(s), ${errors} erreur(s)${premierMotif ? ` — ${premierMotif}` : ''}`,
      });
    } else {
      setMessage({ type: 'success', text: `${ok} matière(s) supprimée(s) avec succès` });
    }
  };

  const handleToggleActif = async (matiere: Matiere) => {
    try {
      const result = await toggleActifMatiere({ id: matiere.id }).unwrap();
      setMessage({ type: 'success', text: result.message });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.detail || 'Erreur lors du changement de statut' });
    }
  };

  const handleToggleActifSelected = async (actif: boolean) => {
    if (selectedMatieres.size === 0) return;
    const label = actif ? 'activer' : 'désactiver';
    if (!window.confirm(`${actif ? 'Activer' : 'Désactiver'} ${selectedMatieres.size} matière(s) sélectionnée(s) ?`)) return;
    try {
      const result = await toggleActifMultiple({ ids: Array.from(selectedMatieres), actif }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setSelectedMatieres(new Set());
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.detail || `Erreur lors de la ${label}` });
    }
  };

  const handleCreateOrUpdateUE = async () => {
    if (!ueForm.code_ue || !ueForm.nom_ue || !ueForm.option) {
      setMessage({ type: 'error', text: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }

    try {
      const ueData = {
        code_ue: ueForm.code_ue,
        nom_ue: ueForm.nom_ue,
        niveau: ueForm.niveau,
        semestre: ueForm.semestre,
        option: ueForm.option,
      };

      if (editingUE) {
        await updateUE({ id: editingUE.id, data: ueData }).unwrap();
        setMessage({ type: 'success', text: 'UE mise a jour avec succes' });
      } else {
        await createUE(ueData).unwrap();
        setMessage({ type: 'success', text: 'UE creee avec succes' });
      }

      setShowUEModal(false);
      resetUEForm();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.detail || 'Erreur lors de la sauvegarde' });
    }
  };

  const handleEditUE = (ue: any) => {
    setEditingUE(ue);
    setUeForm({
      code_ue: ue.code_ue,
      nom_ue: ue.nom_ue,
      niveau: ue.niveau,
      semestre: ue.semestre,
      option: ue.option,
    });
    setShowUEModal(true);
  };

  const handleDeleteUE = async (id: number) => {
    if (!window.confirm('Etes-vous sur de vouloir supprimer cette UE ? Toutes les matieres associees seront supprimees.')) return;
    try {
      await deleteUE(id).unwrap();
      setMessage({ type: 'success', text: 'UE supprimee avec succes' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.detail || 'Erreur lors de la suppression' });
    }
  };

  const toggleUEExpansion = (ueId: number) => {
    setExpandedUEs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ueId)) {
        newSet.delete(ueId);
      } else {
        newSet.add(ueId);
      }
      return newSet;
    });
  };

  // Get matieres for a specific UE
  const getMatieresForUE = (ueId: number) => {
    if (!matieres) return [];
    return matieres.filter((m) => m.ue === ueId);
  };

  const handleCopierUEs = async () => {
    if (!copyForm.option_source || copyForm.options_cibles.length === 0) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner une option source et au moins une option cible' });
      return;
    }
    try {
      const result = await copierUEs({
        option_source: copyForm.option_source,
        options_cibles: copyForm.options_cibles,
        niveau: copyForm.niveau,
      }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setShowCopyModal(false);
      setCopyForm({ option_source: '', options_cibles: [], niveau: 'L1' });
      refetchMatieres();
      refetchUEs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors de la copie' });
    }
  };

  const handleCopierMatieres = async () => {
    if (selectedMatieres.size === 0 || copyMatieresOptionsCibles.length === 0) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner des matières et au moins une option cible' });
      return;
    }
    try {
      const result = await copierMatieres({
        matiere_ids: Array.from(selectedMatieres),
        options_cibles: copyMatieresOptionsCibles,
      }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setShowCopyMatieresModal(false);
      setCopyMatieresOptionsCibles([]);
      setSelectedMatieres(new Set());
      refetchMatieres();
      refetchUEs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors de la copie des matières' });
    }
  };

  const toggleCopyMatiereCible = (optNom: string) => {
    setCopyMatieresOptionsCibles((prev) =>
      prev.includes(optNom) ? prev.filter((o) => o !== optNom) : [...prev, optNom]
    );
  };

  const toggleCibleOption = (optCode: string) => {
    setCopyForm((prev) => ({
      ...prev,
      options_cibles: prev.options_cibles.includes(optCode)
        ? prev.options_cibles.filter((o) => o !== optCode)
        : [...prev.options_cibles, optCode],
    }));
  };

  // Construit les filtres selon le scope de nettoyage
  const buildNettoyageFiltres = (): NettoyageFiltres => {
    const base: NettoyageFiltres = { niveau: cleanupNiveau, scope: cleanupScope };
    if (cleanupScope === 'option') return { ...base, option: cleanupOption };
    if (cleanupScope === 'options') return { ...base, options_liste: cleanupSelectedOptions };
    if (cleanupScope === 'parcours' && cleanupParcours !== '') return { ...base, parcours_id: cleanupParcours as number };
    if (cleanupScope === 'pole' && cleanupPole !== '') return { ...base, pole_id: cleanupPole as number };
    return base; // scope === 'tout'
  };

  const handleApercuNettoyage = async () => {
    if (!cleanupNiveau) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un niveau' });
      return;
    }
    if (cleanupScope === 'option' && !cleanupOption) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner une option' });
      return;
    }
    if (cleanupScope === 'options' && cleanupSelectedOptions.length === 0) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner au moins une option' });
      return;
    }
    if (cleanupScope === 'parcours' && cleanupParcours === '') {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un parcours' });
      return;
    }
    if (cleanupScope === 'pole' && cleanupPole === '') {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un pôle' });
      return;
    }
    const filtres = buildNettoyageFiltres();
    try {
      const result = await apercuNettoyage({ filtres }).unwrap();
      setCleanupApercu(result);
      setCleanupStep(2);
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors de l\'aperçu' });
    }
  };

  const handleTelechargerBackup = () => {
    if (!cleanupApercu) return;
    const json = JSON.stringify(cleanupApercu.backup, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup_ues_matieres_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExecuterNettoyage = async () => {
    const filtres = buildNettoyageFiltres();
    try {
      const result = await executerNettoyage({ filtres }).unwrap();
      setMessage({ type: 'success', text: result.message });
      setShowCleanupModal(false);
      setCleanupStep(1);
      setCleanupApercu(null);
      setCleanupOption('');
      setCleanupSelectedOptions([]);
      setCleanupNiveau('L1');
      setCleanupScope('tout');
      setCleanupPole('');
      setCleanupParcours('');
      refetchMatieres();
      refetchUEs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors du nettoyage' });
    }
  };

  // ── Helpers pour impression / export ──────────────────────────────────────

  const getFormateurNom = (matiereId: number): string => {
    const attr = attributionsAnnee?.[String(matiereId)];
    if (!attr) return '';
    return typeof attr === 'object' && 'formateur_nom' in attr ? attr.formateur_nom : String(attr);
  };

  // Prépare les données pour impression et export
  const preparerDonneesImpression = (sourceList?: Matiere[]) => {
    const list = sourceList || filteredMatieres;
    if (!list || list.length === 0) return null;

    // Grouper par niveau > option (classe)
    type GroupeNiveau = {
      niveau: string;
      classes: Record<string, { classeCode: string; optionNom: string; matieres: Matiere[] }>;
    };
    const parNiveau: Record<string, GroupeNiveau> = {};

    list.forEach((m) => {
      const niv = m.niveau || 'Autre';
      if (!parNiveau[niv]) parNiveau[niv] = { niveau: niv, classes: {} };

      // Tronc commun : pas d'option spécifique
      const isTroncCommun = !m.option || m.option === 'Tronc Commun';
      const classeCode = isTroncCommun ? `${niv}-TC` : `${niv}${optionNomToCode.get(m.option!) || m.option}`;
      const optionLabel = isTroncCommun ? 'Tronc Commun' : (m.option || 'Sans option');

      if (!parNiveau[niv].classes[classeCode]) {
        parNiveau[niv].classes[classeCode] = { classeCode, optionNom: optionLabel, matieres: [] };
      }
      parNiveau[niv].classes[classeCode].matieres.push(m);
    });

    const orderedNiveaux = Object.keys(parNiveau).sort((a, b) =>
      niveauxOrdonnes.indexOf(a) - niveauxOrdonnes.indexOf(b)
    );

    return { parNiveau, orderedNiveaux, totalMatieres: list.length };
  };

  // Impression tableau matières-formateurs (avec filtres optionnels pôle/niveau)
  const handlePrintMatieresFormateurs = (overridePole?: number | '', overrideNiveau?: string, overrideOption?: string) => {
    // Filtrer les matières selon pôle, niveau et option choisis
    const poleId = overridePole !== undefined ? overridePole : filterPole;
    const niveauVal = overrideNiveau !== undefined ? overrideNiveau : filterNiveau;
    const optionVal = overrideOption !== undefined ? overrideOption : '';

    // Exclure le tronc commun de l'impression
    let matieresAPrinter = (matieres || []).filter(m => m.option && m.option !== 'Tronc Commun');

    // Filtrer par pôle : ne garder que les matières dont l'option appartient au pôle sélectionné
    if (poleId) {
      const optionsDuPole = (options || []).filter(o => o.pole === poleId).map(o => o.nom.toLowerCase().trim());
      matieresAPrinter = matieresAPrinter.filter(m => {
        return optionsDuPole.includes((m.option || '').toLowerCase().trim());
      });
    }

    // Filtrer par niveau
    if (niveauVal) {
      matieresAPrinter = matieresAPrinter.filter(m => m.niveau === niveauVal);
    }

    // Filtrer par option (classe)
    if (optionVal) {
      matieresAPrinter = matieresAPrinter.filter(m => m.option === optionVal);
    }

    const data = preparerDonneesImpression(matieresAPrinter);
    if (!data) return;
    const { parNiveau, orderedNiveaux, totalMatieres } = data;

    const annee = anneeActive?.libelle || '—';
    const selectedPole = poleId ? poles?.find((p) => p.id === poleId)?.nom : null;

    let subtitleParts: string[] = [];
    if (selectedPole) subtitleParts.push(`Pôle : ${selectedPole}`);
    if (niveauVal) subtitleParts.push(`Niveau : ${niveauVal}`);
    if (optionVal) subtitleParts.push(`Option : ${optionVal}`);

    let tableRows = '';
    let globalIndex = 0;
    const colCount = 8;

    orderedNiveaux.forEach((niv) => {
      const niveauData = parNiveau[niv];
      // En-tête de niveau
      tableRows += `<tr class="groupe-niveau"><td colspan="${colCount}" style="background:#2c3e50;color:#fff;font-weight:bold;font-size:13px;padding:8px 10px;border:1px solid #999;">${niv}</td></tr>`;

      const classeKeys = Object.keys(niveauData.classes).sort((a, b) => a.localeCompare(b));
      classeKeys.forEach((classeCode) => {
        const group = niveauData.classes[classeCode];
        const isTroncCommun = group.optionNom === 'Tronc Commun';
        const bgColor = isTroncCommun ? '#d5e8d4' : '#e8eef5';
        const label = isTroncCommun
          ? `${niv} — Tronc Commun (${group.matieres.length} matière${group.matieres.length > 1 ? 's' : ''})`
          : `${classeCode} — ${group.optionNom} (${group.matieres.length} matière${group.matieres.length > 1 ? 's' : ''})`;

        tableRows += `<tr class="groupe-classe"><td colspan="${colCount}" style="background:${bgColor};font-weight:bold;font-size:12px;padding:6px 10px;border:1px solid #999;padding-left:20px;">${label}</td></tr>`;

        group.matieres.forEach((m) => {
          globalIndex++;
          const formateurNom = getFormateurNom(m.id);
          tableRows += `<tr>
            <td style="text-align:center;width:30px;">${globalIndex}</td>
            <td style="font-family:monospace;font-size:11px;width:80px;">${m.code_matiere}</td>
            <td>${m.nom_matiere}</td>
            <td style="text-align:center;width:70px;font-size:10px;color:#666;">${m.ue_code || ''}</td>
            <td style="width:140px;font-size:10px;color:#666;border-style:dashed;"></td>
            <td style="text-align:center;width:30px;">S${m.semestre || ''}</td>
            <td style="width:170px;">${formateurNom || '<span style="color:#c00;font-style:italic;">Non attribué</span>'}</td>
            <td style="width:50px;text-align:center;">&square;</td>
          </tr>`;
        });
      });
    });

    const today = new Date();
    const dateStr = today.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Liste Matières-Formateurs</title>
<style>
  @page { margin: 12mm 10mm; size: A4 landscape; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Tahoma, sans-serif; font-size: 11px; color: #222; line-height: 1.35; }
  .header { text-align: center; margin-bottom: 14px; }
  .header h1 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
  .header .annee { font-size: 12px; font-weight: bold; margin-bottom: 3px; }
  .header .filtres { font-size: 10.5px; color: #666; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  th { background: #34495e; color: #fff; font-size: 10px; text-transform: uppercase; padding: 5px 6px; text-align: left; }
  td { padding: 4px 6px; border: 1px solid #bbb; font-size: 11px; }
  tr:nth-child(even):not(.groupe-classe):not(.groupe-niveau) { background: #fafafa; }
  .groupe-niveau td { background: #2c3e50; color: #fff; }
  .groupe-classe td { background: #e8eef5; }
  .legende { margin-top: 8px; font-size: 10px; color: #555; }
  .legende span { color: #c00; }
  .signatures { margin-top: 25px; display: flex; justify-content: space-between; }
  .signatures .bloc { width: 30%; text-align: center; }
  .signatures .titre { font-size: 10px; font-weight: bold; margin-bottom: 4px; border-bottom: 1px solid #999; padding-bottom: 2px; }
  .signatures .zone { height: 55px; border-bottom: 1px dotted #999; margin-bottom: 3px; }
  .signatures .label { font-size: 9px; color: #777; }
  .footer { margin-top: 15px; text-align: center; font-size: 9px; color: #999; border-top: 1px solid #ddd; padding-top: 4px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head>
<body>
  <div class="header">
    <h1>Liste des Matières, UE et Formateurs</h1>
    <div class="annee">Année Académique : ${annee}</div>
    ${subtitleParts.length > 0 ? `<div class="filtres">${subtitleParts.join(' &bull; ')}</div>` : ''}
  </div>

  <table>
    <thead>
      <tr>
        <th style="width:30px;text-align:center;">N°</th>
        <th style="width:80px;">Code</th>
        <th>Matière</th>
        <th style="width:70px;text-align:center;">UE (code)</th>
        <th style="width:140px;">Proposition UE</th>
        <th style="width:30px;text-align:center;">Sem.</th>
        <th style="width:170px;">Formateur</th>
        <th style="width:50px;text-align:center;">Faite</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>

  <div class="legende">
    <strong>Légende :</strong> &square; = Matière non encore vérifiée &bull;
    <span>Non attribué</span> = Aucun formateur assigné &bull;
    Colonne « Proposition UE » : à remplir par le coordonnateur de pôle
    <br/>Total : ${totalMatieres} matière${totalMatieres > 1 ? 's' : ''}
  </div>

  <div class="signatures">
    <div class="bloc">
      <div class="titre">Le Coordonnateur de Pôle</div>
      <div class="zone"></div>
      <div class="label">Nom, Prénom &amp; Signature</div>
    </div>
    <div class="bloc">
      <div class="titre">Le Coordonnateur de Parcours</div>
      <div class="zone"></div>
      <div class="label">Nom, Prénom &amp; Signature</div>
    </div>
    <div class="bloc">
      <div class="titre">Le Directeur Académique</div>
      <div class="zone"></div>
      <div class="label">Nom, Prénom &amp; Signature</div>
    </div>
  </div>

  <div class="footer">
    Document généré le ${dateStr} &mdash; Système de Gestion des Notes
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => { printWindow.print(); };
    }
  };

  // Export Excel matières-formateurs-UE
  const handleExportExcel = () => {
    const data = preparerDonneesImpression();
    if (!data) return;
    const { parNiveau, orderedNiveaux } = data;

    const annee = anneeActive?.libelle || '';
    const selectedPole = filterPole ? poles?.find((p) => p.id === filterPole)?.nom : '';

    // Construire le CSV (compatible Excel avec séparateur ;)
    const BOM = '\uFEFF'; // UTF-8 BOM pour que Excel lise les accents
    let csv = BOM;
    csv += `Liste des Matières et Formateurs;;;;;;\n`;
    csv += `Année Académique : ${annee};;;;;;\n`;
    if (selectedPole) csv += `Pôle : ${selectedPole};;;;;;\n`;
    csv += `;;;;;;\n`;
    csv += `N°;Code;Matière;UE (code existant);Proposition nom UE (à remplir);Semestre;Formateur\n`;

    let idx = 0;
    orderedNiveaux.forEach((niv) => {
      const niveauData = parNiveau[niv];
      csv += `;;;;;;\n`;
      csv += `${niv};;;;;;\n`;

      const classeKeys = Object.keys(niveauData.classes).sort();
      classeKeys.forEach((classeCode) => {
        const group = niveauData.classes[classeCode];
        csv += `${classeCode} — ${group.optionNom};;;;;;\n`;

        group.matieres.forEach((m) => {
          idx++;
          const fNom = getFormateurNom(m.id);
          const escapeCsv = (s: string) => s.includes(';') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
          csv += `${idx};${m.code_matiere};${escapeCsv(m.nom_matiere)};${m.ue_code || ''};;"S${m.semestre || ''}";${escapeCsv(fNom || 'Non attribué')}\n`;
        });
      });
    });

    // Télécharger
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matieres-formateurs-${annee.replace('/', '-') || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isLoading = matieresLoading || uesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const availableSemestres = filterNiveau ? getSemestresForNiveau(filterNiveau) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestion des Matieres et UEs</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Gerez les unites d'enseignement et les matieres</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            icon={<Printer size={18} />}
            onClick={() => { setPrintPole(filterPole); setPrintNiveau(filterNiveau); setPrintOption(filterOption); setShowPrintModal(true); }}
            disabled={!matieres || matieres.length === 0}
          >
            Imprimer liste
          </Button>
          <Button
            variant="outline"
            icon={<Download size={18} />}
            onClick={handleExportExcel}
            disabled={!filteredMatieres || filteredMatieres.length === 0}
          >
            Exporter Excel
          </Button>
          <Button variant="outline" icon={<Copy size={18} />} onClick={() => setShowCopyModal(true)}>
            Copier vers une option
          </Button>
          <Button
            variant="danger"
            icon={<Trash size={18} />}
            onClick={() => { setCleanupStep(1); setCleanupApercu(null); setShowCleanupModal(true); }}
          >
            Nettoyage
          </Button>
          <Button
            variant="outline"
            icon={<Calendar size={18} />}
            onClick={() => setShowSujetsModal(true)}
          >
            Nb Sujets
          </Button>
          <Button variant="outline" icon={<RefreshCw size={18} />} onClick={handleRefresh}>
            Actualiser
          </Button>
        </div>
      </div>

      {/* Alerte matières orphelines */}
      {orphelinesData && orphelinesData.count > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
                {orphelinesData.count} matière(s) sans option (orphelines)
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Ces matières n'ont pas d'UE et donc pas d'option associée. Elles apparaissent sous "L1", "L2", "L3" sans classe.
              </p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => {
                    setRattachSelectedIds(new Set(orphelinesData.matieres.map(m => m.id)));
                    setRattachOption('');
                    setShowRattachModal(true);
                  }}
                  className="text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800/40 px-3 py-1.5 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-800/60 transition"
                >
                  Rattacher à une option
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await nettoyerOrphelines({ mode: 'analyser' }).unwrap();
                      setNettoyerAnalyse(res);
                      setShowNettoyerModal(true);
                    } catch (err: any) {
                      setMessage({ type: 'error', text: err?.data?.error || 'Erreur lors de l\'analyse' });
                    }
                  }}
                  disabled={nettoyerLoading}
                  className="text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 px-3 py-1.5 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/60 transition"
                >
                  {nettoyerLoading ? 'Analyse...' : 'Supprimer les orphelines'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
          }`}
        >
          {message.text}
          <button className="float-right font-bold" onClick={() => setMessage(null)}>
            x
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('matieres')}
          className={`pb-3 px-1 font-medium ${
            activeTab === 'matieres'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Matieres ({matieres?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('diagnostic')}
          className={`pb-3 px-1 font-medium ${
            activeTab === 'diagnostic'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Diagnostic
          {(matieresSansFormateur.length > 0 || optionsSansMatiere.length > 0) && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
              {matieresSansFormateur.length + optionsSansMatiere.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('recapitulatif')}
          className={`pb-3 px-1 font-medium ${
            activeTab === 'recapitulatif'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Récapitulatif
        </button>
        <button
          onClick={() => setActiveTab('ues')}
          className={`pb-3 px-1 font-medium ${
            activeTab === 'ues'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Unites d'Enseignement ({ues?.length || 0})
        </button>
        {/* Fix 2026-09-09 : onglet dédié aux matières désactivées */}
        <button
          onClick={() => setActiveTab('desactivees')}
          className={`pb-3 px-1 font-medium ${
            activeTab === 'desactivees'
              ? 'text-orange-600 border-b-2 border-orange-600'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Désactivées
          {matieresDesactivees.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full">
              {matieresDesactivees.length}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <Card>
        <div className="space-y-3">
          {/* Ligne 1 : Recherche + Niveau + Semestre */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Rechercher par code ou nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={filterNiveau}
              onChange={(e) => {
                setFilterNiveau(e.target.value);
                setFilterSemestre('');
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les niveaux</option>
              {NIVEAUX.map((n) => (
                <option key={n.value} value={n.value}>{n.label}</option>
              ))}
            </select>
            <select
              value={filterSemestre}
              onChange={(e) => setFilterSemestre(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              disabled={!filterNiveau}
            >
              <option value="">Tous les semestres</option>
              {availableSemestres.map((s) => (
                <option key={s.value} value={s.value.toString()}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Ligne 2 : Type formation → Pôle → Parcours → Option (cascade) */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select
              value={filterFormation}
              onChange={(e) => {
                setFilterFormation(e.target.value as '' | 'FI' | 'CS');
                setFilterOption('');
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              title="Filtrer par type de formation (FI = Formation Initiale, CS = Cours du Soir)"
            >
              <option value="">FI + CS</option>
              <option value="FI">FI uniquement (Formation Initiale)</option>
              <option value="CS">CS uniquement (Formation Continue)</option>
            </select>
            <select
              value={filterPole}
              onChange={(e) => {
                setFilterPole(e.target.value ? parseInt(e.target.value) : '');
                setFilterParcours('');
                setFilterOption('');
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les pôles</option>
              {poles?.map((p) => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))}
            </select>
            <select
              value={filterParcours}
              onChange={(e) => {
                setFilterParcours(e.target.value ? parseInt(e.target.value) : '');
                setFilterOption('');
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous les parcours</option>
              {(filterPole
                ? parcoursList?.filter((p) => p.pole === filterPole)
                : parcoursList
              )?.map((p) => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))}
            </select>
            <select
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Toutes les options</option>
              {optionsFiltered.map((o) => (
                <option key={o.id} value={o.nom}>{o.code} — {o.nom}</option>
              ))}
            </select>
            {(filterPole || filterParcours || filterOption || filterNiveau || filterSemestre || searchTerm || filterFormation) && (
              <button
                onClick={() => {
                  setFilterPole('');
                  setFilterParcours('');
                  setFilterOption('');
                  setFilterNiveau('');
                  setFilterSemestre('');
                  setSearchTerm('');
                  setFilterFormation('');
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <X size={14} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Bannière "Cours du Soir" :
          - Visible en permanence avec un bouton d'accès au volet de duplication.
          - Affiche un appel à l'action plus visible si filtre CS actif et qu'aucune option CS n'existe encore. */}
      <div className={`flex items-start gap-3 p-4 rounded-lg border ${
        filterFormation === 'CS' && totalOptionsCS === 0
          ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700'
          : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
      }`}>
        <Moon size={20} className="text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Cours du Soir (CS)
          </div>
          {filterFormation === 'CS' && totalOptionsCS === 0 ? (
            <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5">
              Aucune option CS n'est encore configurée alors que des étudiants Cours du Soir existent
              dans la base. Utilisez le volet ci-contre pour copier automatiquement les options et matières
              depuis la Formation Initiale en tenant compte des étudiants importés.
            </p>
          ) : (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {totalOptionsCS > 0
                ? `${totalOptionsCS} option${totalOptionsCS > 1 ? 's' : ''} CS configurée${totalOptionsCS > 1 ? 's' : ''}. `
                : 'Aucune option CS configurée pour l\'instant. '}
              Le volet « Configurer les CS depuis FI » détecte les étudiants CS importés et duplique les options
              FI correspondantes (matières + UE + classes), en respectant les options présentes en BDD.
            </p>
          )}
        </div>
        <button
          onClick={() => setShowAutoCSModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex-shrink-0 transition-colors"
          title="Détecter les étudiants CS et auto-créer les options + matières depuis FI"
        >
          <Users size={14} />
          Configurer les CS depuis FI
        </button>
      </div>

      {/* MATIERES TAB */}
      {activeTab === 'matieres' && (
        <div className="space-y-4">
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              icon={<Plus size={18} />}
              onClick={() => {
                // Hérite du filtre de formation actif dans la page (si CS-only, batch CS-only)
                setBatchFormationFilter(filterFormation);
                setBatchForm({ noms: '', niveau: 'L1', semestre: 1, selectedOptionIds: [] });
                setShowBatchModal(true);
              }}
            >
              Insertion en lot
            </Button>
            <Button
              variant="primary"
              icon={<Plus size={18} />}
              onClick={() => {
                resetMatiereForm();
                setShowMatiereModal(true);
              }}
            >
              Nouvelle Matière
            </Button>
          </div>

          {/* Barre d'actions groupées */}
          {selectedMatieres.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {selectedMatieres.size} matière{selectedMatieres.size > 1 ? 's' : ''} sélectionnée{selectedMatieres.size > 1 ? 's' : ''}
              </span>
              <div className="flex-1" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedMatieres(new Set())}
              >
                Désélectionner
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleToggleActifSelected(false)}
              >
                Désactiver
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleToggleActifSelected(true)}
              >
                Activer
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<Copy size={14} />}
                onClick={() => { setCopyMatieresOptionsCibles([]); setShowCopyMatieresModal(true); }}
              >
                Copier vers option
              </Button>
              <Button
                variant="danger"
                size="sm"
                icon={<Trash2 size={14} />}
                onClick={handleDeleteSelectedMatieres}
                disabled={deletingBatch}
              >
                {deletingBatch ? 'Suppression...' : 'Supprimer la sélection'}
              </Button>
            </div>
          )}

          {/* Résumé + tout déplier/replier */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredMatieres.length} matière{filteredMatieres.length !== 1 ? 's' : ''} dans {matieresGroupKeys.length} classe{matieresGroupKeys.length !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setExpandedMatiereGroups(new Set(matieresGroupKeys))}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Tout déplier
              </button>
              <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
              <button
                onClick={() => setExpandedMatiereGroups(new Set())}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Tout replier
              </button>
              <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filteredMatieres.length > 0 && selectedMatieres.size === filteredMatieres.length}
                  onChange={handleSelectAllMatieres}
                  className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                />
                Tout sélectionner
              </label>
            </div>
          </div>

          {filteredMatieres.length === 0 ? (
            <Card>
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                Aucune matière trouvée
              </div>
            </Card>
          ) : (
            <div className="space-y-2">
              {matieresGroupKeys.map((classeCode) => {
                const group = matieresGroupees[classeCode];
                const isExpanded = expandedMatiereGroups.has(classeCode);
                const groupSelectedCount = group.matieres.filter((m) => selectedMatieres.has(m.id)).length;

                return (
                  <Card key={classeCode} className="overflow-hidden">
                    {/* En-tête de groupe */}
                    <button
                      onClick={() => setExpandedMatiereGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(classeCode)) next.delete(classeCode); else next.add(classeCode);
                        return next;
                      })}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      {isExpanded ? <ChevronDown size={18} className="text-gray-400" /> : <ChevronRight size={18} className="text-gray-400" />}
                      <span className="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-sm font-bold">
                        {classeCode}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {group.optionNom}
                      </span>
                      <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                        {group.matieres.length} matière{group.matieres.length > 1 ? 's' : ''}
                        {groupSelectedCount > 0 && (
                          <span className="ml-1.5 text-blue-600 dark:text-blue-400">({groupSelectedCount} sel.)</span>
                        )}
                      </span>
                    </button>

                    {/* Table des matières du groupe */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                              <th className="px-3 py-2 text-center w-10">
                                <input
                                  type="checkbox"
                                  checked={group.matieres.length > 0 && groupSelectedCount === group.matieres.length}
                                  onChange={() => {
                                    setSelectedMatieres((prev) => {
                                      const next = new Set(prev);
                                      const allSelected = group.matieres.every((m) => next.has(m.id));
                                      group.matieres.forEach((m) => { if (allSelected) next.delete(m.id); else next.add(m.id); });
                                      return next;
                                    });
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                                  title={`Sélectionner tout ${classeCode}`}
                                />
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Code</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Nom</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 w-14">Sem.</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 w-16">Crédits</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-24">UE</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 w-16">Statut</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-24">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                            {group.matieres.map((matiere) => (
                              <tr
                                key={matiere.id}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                                  selectedMatieres.has(matiere.id) ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                                } ${matiere.actif === false ? 'opacity-50' : ''}`}
                              >
                                <td className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedMatieres.has(matiere.id)}
                                    onChange={() => handleToggleSelectMatiere(matiere.id)}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{matiere.code_matiere}</td>
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                  {matiere.nom_matiere}
                                  {matiere.actif === false && (
                                    <span className="ml-2 text-xs text-red-500 dark:text-red-400">(inactive)</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center text-gray-500 dark:text-gray-400">S{matiere.semestre}</td>
                                <td className="px-3 py-2 text-center font-semibold">{matiere.credits}</td>
                                <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                                  {matiere.ue_code || (
                                    <span className="text-orange-500 dark:text-orange-400">—</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <button
                                    onClick={() => handleToggleActif(matiere)}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                                      matiere.actif !== false
                                        ? 'bg-green-500'
                                        : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                    title={matiere.actif !== false ? 'Désactiver cette matière' : 'Activer cette matière'}
                                  >
                                    <span
                                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                                        matiere.actif !== false ? 'translate-x-[18px]' : 'translate-x-[2px]'
                                      }`}
                                    />
                                  </button>
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <div className="flex justify-end space-x-1">
                                    <Button variant="outline" size="sm" icon={<Edit size={14} />} onClick={() => handleEditMatiere(matiere)} />
                                    <Button variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={() => handleDeleteMatiere(matiere.id)} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* DIAGNOSTIC TAB */}
      {activeTab === 'diagnostic' && (
        <div className="space-y-6">
          {/* Section 1 : Matières sans formateur */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle size={20} className="text-amber-500" />
                Matières sans formateur
                <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full font-medium">
                  {matieresSansFormateur.length}
                </span>
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Matières non attribuées pour {anneeActive?.libelle || '—'}. Vous pouvez attribuer un formateur directement ici.
              </p>
            </div>
            <div className="p-4">
              {matieresSansFormateur.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                  <CheckCircle size={40} className="mx-auto mb-2 text-green-500" />
                  <p>Toutes les matières ont un formateur attribué</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {diagClassesOrdonnees.map((classeCode) => {
                    const group = diagMatieresParClasse[classeCode];
                    const isExpanded = diagExpandedClasses.has(classeCode);

                    return (
                      <div key={classeCode}>
                        <button
                          onClick={() => setDiagExpandedClasses((prev) => {
                            const next = new Set(prev);
                            if (next.has(classeCode)) next.delete(classeCode); else next.add(classeCode);
                            return next;
                          })}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-bold">
                            {classeCode}
                          </span>
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            {group.matieres.length} matière{group.matieres.length > 1 ? 's' : ''} sans formateur
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="mt-1 ml-2 space-y-1">
                            {group.matieres.map((m) => {
                              const isOrpheline = !m.ue;
                              const hasDuplicate = isOrpheline && m.niveau && m.nom_matiere && matiereOptionIndex.has(`${m.niveau}|${m.nom_matiere}`);
                              return (
                              <div
                                key={m.id}
                                className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-700/30 rounded-md"
                              >
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-1.5">
                                      {m.code_matiere}
                                    </span>
                                    {m.nom_matiere}
                                  </span>
                                  {m.semestre && (
                                    <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500">S{m.semestre}</span>
                                  )}
                                  {isOrpheline && (
                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                                      sans UE{hasDuplicate ? ' (doublon)' : ''}
                                    </span>
                                  )}
                                </div>
                                {isOrpheline ? (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm(`Supprimer la matière orpheline "${m.nom_matiere}" (${m.code_matiere}) ?${hasDuplicate ? '\nCette matière existe déjà avec une UE, la suppression est sans risque.' : '\nAttention : cette matière n\'a pas de doublon.'}`)) return;
                                      try {
                                        await deleteMatiere(m.id).unwrap();
                                      } catch (err: any) {
                                        alert(err?.data?.detail
                                          || 'Erreur lors de la suppression. La matière est peut-être liée à des notes.');
                                      }
                                    }}
                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                    title="Supprimer cette matière orpheline"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                ) : (
                                  <>
                                    <FormateurSearchSelect
                                      formateurs={formateurs || []}
                                      value={diagFormateurSelections[m.id]}
                                      onChange={(id) => setDiagFormateurSelections((prev) => ({
                                        ...prev,
                                        [m.id]: id,
                                      }))}
                                    />
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      disabled={!diagFormateurSelections[m.id]}
                                      onClick={() => handleAssignerFormateurDiag(m.id)}
                                    >
                                      Attribuer
                                    </Button>
                                  </>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Section 1-bis : Matières attribuées (avec changement inline) */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <CheckCircle size={20} className="text-green-500" />
                Matières attribuées à un formateur
                <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full font-medium">
                  {matieresAvecFormateur.length}
                </span>
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Matières déjà attribuées pour {anneeActive?.libelle || '—'}. Cliquez « Changer » pour remplacer le formateur (filtres de la page appliqués).
              </p>
            </div>
            <div className="p-4">
              {matieresAvecFormateur.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                  <p className="text-sm">Aucune matière attribuée pour les filtres actuels.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {diagAttribueesClassesOrdonnees.map((classeCode) => {
                    const group = diagAttribueesParClasse[classeCode];
                    const isExpanded = diagAttribueesExpanded.has(classeCode);
                    return (
                      <div key={classeCode}>
                        <button
                          onClick={() => setDiagAttribueesExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(classeCode)) next.delete(classeCode); else next.add(classeCode);
                            return next;
                          })}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-bold">
                            {classeCode}
                          </span>
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                            {group.matieres.length} matière{group.matieres.length > 1 ? 's' : ''} attribuée{group.matieres.length > 1 ? 's' : ''}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="mt-1 ml-2 space-y-1">
                            {group.matieres.map((m) => {
                              const attr = attributionsAnnee?.[String(m.id)];
                              const enEdition = diagEditionMatiereId === m.id;
                              return (
                                <div
                                  key={m.id}
                                  className="flex items-center gap-3 px-3 py-2 bg-green-50/50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 rounded-md"
                                >
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-gray-800 dark:text-gray-200">
                                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-1.5">
                                        {m.code_matiere}
                                      </span>
                                      {m.nom_matiere}
                                    </span>
                                    {m.semestre && (
                                      <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500">S{m.semestre}</span>
                                    )}
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                      <span className="text-[11px] text-gray-500 dark:text-gray-400">Formateur actuel :</span>
                                      <span className="text-[11px] font-medium text-green-700 dark:text-green-400">
                                        {attr?.formateur_nom || '—'}
                                      </span>
                                    </div>
                                  </div>
                                  {enEdition ? (
                                    <>
                                      <FormateurSearchSelect
                                        formateurs={formateurs || []}
                                        value={diagChangementSelections[m.id]}
                                        onChange={(id) => setDiagChangementSelections((prev) => ({
                                          ...prev,
                                          [m.id]: id,
                                        }))}
                                      />
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        disabled={!diagChangementSelections[m.id]}
                                        onClick={() => handleChangerFormateurDiag(m.id)}
                                      >
                                        Confirmer
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setDiagEditionMatiereId(null);
                                          setDiagChangementSelections((prev) => { const next = { ...prev }; delete next[m.id]; return next; });
                                        }}
                                      >
                                        Annuler
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setDiagEditionMatiereId(m.id)}
                                    >
                                      Changer
                                    </Button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Section 1.5 : Paires de classes similaires (≥90% matières communes) */}
          <PairesSimilairesSection anneeLibelle={anneeActive?.libelle || ''} />

          {/* Section 2 : Options sans matière */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <AlertTriangle size={20} className="text-red-500" />
                Options sans matière
                <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full font-medium">
                  {optionsSansMatiere.length}
                </span>
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Options actives sans matière associée. Vous pouvez ajouter des matières ou supprimer les options inutiles.
              </p>
            </div>
            <div className="p-4">
              {optionsSansMatiere.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                  <CheckCircle size={40} className="mx-auto mb-2 text-green-500" />
                  <p>Toutes les options ont des matières</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {optionsSansMatiere.map((opt) => (
                    <div
                      key={opt.id}
                      className="flex items-center gap-3 px-4 py-3 border border-red-200 dark:border-red-800/30 bg-red-50 dark:bg-red-900/10 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {opt.nom}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {opt.code} · {opt.pole_nom}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Plus size={14} />}
                          onClick={() => {
                            // Auto-détecter le type_formation depuis l'option cliquée
                            const isCS = (opt.code || '').endsWith('-CS');
                            setBatchFormationFilter(isCS ? 'CS' : 'FI');
                            setBatchForm({ noms: '', niveau: 'L1', semestre: 1, selectedOptionIds: [opt.id] });
                            setShowBatchModal(true);
                          }}
                        >
                          Ajouter
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          icon={<Trash2 size={14} />}
                          onClick={async () => {
                            if (!window.confirm(
                              `Supprimer l'option "${opt.nom}" (${opt.code}) ?\n\nCette option n'a aucune matière. La suppression est sans risque.`
                            )) return;
                            try {
                              await deleteOption(opt.id).unwrap();
                              setMessage({ type: 'success', text: `Option "${opt.nom}" supprimée` });
                            } catch (err: any) {
                              setMessage({ type: 'error', text: err?.data?.detail || 'Erreur lors de la suppression' });
                            }
                          }}
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Section 3 : Suggestions intelligentes d'affectation */}
          <Card>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Eye size={20} className="text-blue-500" />
                Suggestions d'affectation
                {suggestionsData && (suggestionsData.stats.total_suggestions_propagation > 0) && (
                  <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-medium">
                    {suggestionsData.stats.total_suggestions_propagation} proposition{suggestionsData.stats.total_suggestions_propagation > 1 ? 's' : ''}
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Le système détecte les matières identiques entre options et propose de propager les formateurs déjà assignés.
              </p>
            </div>
            <div className="p-4 space-y-4">
              {!suggestionsData || (
                suggestionsData.stats.total_suggestions_propagation === 0 &&
                suggestionsData.stats.total_options_communes === 0
              ) ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                  <CheckCircle size={40} className="mx-auto mb-2 text-green-500" />
                  <p>Aucune suggestion — tous les formateurs sont déjà propagés</p>
                </div>
              ) : (
                <>
                  {/* Options avec matières communes */}
                  {suggestionsData.options_communes.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                        Options avec matières communes
                      </h4>
                      {suggestionsData.options_communes.map((grp, gi) => (
                        <div key={gi} className="border border-purple-200 dark:border-purple-800/40 rounded-lg overflow-hidden">
                          <div className="px-4 py-2.5 bg-purple-50 dark:bg-purple-900/20 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <Badge variant="info">{grp.niveau}</Badge>
                              <span className="font-medium text-gray-800 dark:text-gray-200">{grp.option_a}</span>
                              <span className="text-gray-400">↔</span>
                              <span className="font-medium text-gray-800 dark:text-gray-200">{grp.option_b}</span>
                              <span className="text-xs text-purple-600 dark:text-purple-400 ml-1">
                                {grp.taux_commun}% de matières communes ({grp.nb_communes}/{grp.nb_total})
                              </span>
                            </div>
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={appliquantSuggestions || grp.propagations.length === 0}
                              onClick={async () => {
                                const attrs = grp.propagations.map(p => ({
                                  matiere_id: p.matiere_id_cible,
                                  formateur_id: p.formateur_id,
                                }));
                                try {
                                  const result = await appliquerSuggestions({
                                    annee_academique: anneeActive!.libelle,
                                    attributions: attrs,
                                  }).unwrap();
                                  setMessage({
                                    type: 'success',
                                    text: `${result.crees} attribution(s) créée(s)${result.conflits.length > 0 ? `, ${result.conflits.length} conflit(s)` : ''}`,
                                  });
                                  refetchSuggestions();
                                } catch {
                                  setMessage({ type: 'error', text: 'Erreur lors de l\'application' });
                                }
                              }}
                            >
                              Appliquer tout ({grp.propagations.length})
                            </Button>
                          </div>
                          <div className="divide-y divide-purple-100 dark:divide-purple-800/30">
                            {grp.propagations.map((p, pi) => (
                              <div key={pi} className="px-4 py-2 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className="text-gray-700 dark:text-gray-300 truncate">{p.matiere_nom}</span>
                                  <span className="text-xs text-gray-400 flex-shrink-0">
                                    {p.option_source} → {p.option_cible}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{p.formateur_nom}</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={appliquantSuggestions}
                                    onClick={async () => {
                                      try {
                                        await attribuerMatiere({
                                          formateur_id: p.formateur_id,
                                          matiere_id: p.matiere_id_cible,
                                          annee_academique: anneeActive!.libelle,
                                        }).unwrap();
                                        setMessage({ type: 'success', text: `${p.matiere_nom} → ${p.formateur_nom}` });
                                        refetchSuggestions();
                                      } catch (err: any) {
                                        setMessage({ type: 'error', text: err?.data?.message || 'Erreur' });
                                      }
                                    }}
                                  >
                                    Appliquer
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Propagations individuelles (même matière dans d'autres options) */}
                  {suggestionsData.propagations.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          Même matière, formateur déjà assigné ailleurs
                        </h4>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={appliquantSuggestions}
                          onClick={async () => {
                            const attrs = suggestionsData.propagations.map(p => ({
                              matiere_id: p.matiere_id_cible,
                              formateur_id: p.formateur_id,
                            }));
                            try {
                              const result = await appliquerSuggestions({
                                annee_academique: anneeActive!.libelle,
                                attributions: attrs,
                              }).unwrap();
                              setMessage({
                                type: 'success',
                                text: `${result.crees} attribution(s) propagée(s)${result.conflits.length > 0 ? `, ${result.conflits.length} conflit(s)` : ''}`,
                              });
                              refetchSuggestions();
                            } catch {
                              setMessage({ type: 'error', text: 'Erreur lors de la propagation' });
                            }
                          }}
                        >
                          Tout propager ({suggestionsData.propagations.length})
                        </Button>
                      </div>
                      <div className="border border-green-200 dark:border-green-800/40 rounded-lg divide-y divide-green-100 dark:divide-green-800/30 max-h-96 overflow-y-auto">
                        {suggestionsData.propagations.map((p, pi) => (
                          <div key={pi} className="px-4 py-2 flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Badge variant="default" className="text-[10px]">{p.niveau}</Badge>
                              <span className="text-gray-700 dark:text-gray-300 truncate">{p.matiere_nom}</span>
                              <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {p.option_source} → <span className="font-medium text-green-700 dark:text-green-400">{p.option_cible}</span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-blue-600 dark:text-blue-400">{p.formateur_nom}</span>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={appliquantSuggestions}
                                onClick={async () => {
                                  try {
                                    await attribuerMatiere({
                                      formateur_id: p.formateur_id,
                                      matiere_id: p.matiere_id_cible,
                                      annee_academique: anneeActive!.libelle,
                                    }).unwrap();
                                    setMessage({ type: 'success', text: `${p.matiere_nom} → ${p.formateur_nom} (${p.option_cible})` });
                                    refetchSuggestions();
                                  } catch (err: any) {
                                    setMessage({ type: 'error', text: err?.data?.message || 'Erreur' });
                                  }
                                }}
                              >
                                Appliquer
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Info : formateurs multi-options */}
                  {suggestionsData.formateurs_multi_options.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        Formateurs enseignant dans plusieurs options
                        <span className="text-xs text-gray-400 font-normal">(info)</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {suggestionsData.formateurs_multi_options.slice(0, 12).map((fm, i) => (
                          <div key={i} className="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/15 border border-indigo-200 dark:border-indigo-800/40 rounded-lg text-xs">
                            <span className="font-medium text-indigo-800 dark:text-indigo-300">{fm.formateur_nom}</span>
                            <span className="text-gray-500 ml-1">— {fm.matiere_nom}</span>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {fm.options.map((opt, oi) => (
                                <Badge key={oi} variant="info" className="text-[9px]">{fm.niveau} {opt}</Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* RECAPITULATIF TAB */}
      {activeTab === 'recapitulatif' && (() => {
        // Organiser par Pôle > Parcours > Classe > Matières
        type RecapMatiere = { id: number; code: string; nom: string; semestre?: number; ue_code?: string; formateur: string };
        type RecapClasse = { code: string; niveau: string; optionNom: string; matieres: RecapMatiere[] };
        type RecapParcours = { nom: string; poleNom: string; classes: RecapClasse[] };

        const parcoursMap = new Map<string, RecapParcours>();
        const niveauxOrd = ['L1', 'L2', 'L3', 'M1', 'M2'];

        // Construire les classes depuis les données
        const allClasses = classes || [];
        const allMatieres = matieres || [];
        const attrs = attributionsAnnee || {};

        // Grouper les matières par option+niveau
        const matsByOptionNiveau = new Map<string, RecapMatiere[]>();
        for (const m of allMatieres) {
          const key = `${m.option || ''}|||${m.niveau || ''}`;
          if (!matsByOptionNiveau.has(key)) matsByOptionNiveau.set(key, []);
          const attr = attrs[String(m.id)];
          const fNom = attr && typeof attr === 'object' && 'formateur_nom' in attr ? attr.formateur_nom : '';
          matsByOptionNiveau.get(key)!.push({
            id: m.id,
            code: m.code_matiere,
            nom: m.nom_matiere,
            semestre: m.semestre,
            ue_code: m.ue_code || undefined,
            formateur: fNom,
          });
        }

        // Pour chaque classe, trouver le parcours et regrouper
        for (const cl of allClasses) {
          const parcoursNom = cl.parcours_nom || 'Sans parcours';
          const poleNom = cl.pole_nom || '';

          if (!parcoursMap.has(parcoursNom)) {
            parcoursMap.set(parcoursNom, { nom: parcoursNom, poleNom, classes: [] });
          }

          const key = `${cl.option_nom}|||${cl.niveau}`;
          const classMatieres = matsByOptionNiveau.get(key) || [];

          // Aussi ajouter les matières Tronc Commun de ce niveau
          const tcKey = `Tronc Commun|||${cl.niveau}`;
          const tcMatieres = matsByOptionNiveau.get(tcKey) || [];

          const allClassMatieres = [...classMatieres, ...tcMatieres].sort((a, b) => {
            if ((a.semestre || 0) !== (b.semestre || 0)) return (a.semestre || 0) - (b.semestre || 0);
            return a.nom.localeCompare(b.nom);
          });

          if (allClassMatieres.length > 0) {
            parcoursMap.get(parcoursNom)!.classes.push({
              code: cl.code,
              niveau: cl.niveau,
              optionNom: cl.option_nom,
              matieres: allClassMatieres,
            });
          }
        }

        // Trier les classes dans chaque parcours par niveau
        Array.from(parcoursMap.values()).forEach((p) => {
          p.classes.sort((a, b) => {
            const nA = niveauxOrdonnes.indexOf(a.niveau);
            const nB = niveauxOrdonnes.indexOf(b.niveau);
            if (nA !== nB) return nA - nB;
            return a.code.localeCompare(b.code);
          });
        });

        // Trier les parcours par pôle puis nom
        const parcoursSorted = (Array.from(parcoursMap.values()) as RecapParcours[]).sort((a: RecapParcours, b: RecapParcours) => {
          if (a.poleNom !== b.poleNom) return a.poleNom.localeCompare(b.poleNom);
          return a.nom.localeCompare(b.nom);
        });

        const handlePrintRecap = () => {
          const w = window.open('', '_blank');
          if (!w) return;
          let html = `<html><head><title>Récapitulatif Matières-Formateurs par Parcours</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
  h1 { font-size: 16px; text-align: center; margin-bottom: 5px; }
  h2 { font-size: 13px; color: #1a56db; margin: 18px 0 6px; border-bottom: 2px solid #1a56db; padding-bottom: 3px; }
  h3 { font-size: 11px; margin: 10px 0 4px; color: #333; background: #f0f0f0; padding: 4px 8px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th, td { border: 1px solid #ccc; padding: 3px 6px; text-align: left; }
  th { background: #2563eb; color: white; font-size: 10px; }
  .tc { background: #f0fdf4; font-style: italic; }
  .no-formateur { color: #dc2626; font-style: italic; }
  .sub { font-size: 10px; text-align: center; color: #666; margin-bottom: 15px; }
  @media print { body { margin: 10px; } }
  @page { size: landscape A4; margin: 10mm; }
</style></head><body>`;
          html += `<h1>Récapitulatif des Matières et Formateurs</h1>`;
          html += `<div class="sub">${anneeActive?.libelle || ''} — Généré le ${new Date().toLocaleDateString('fr-FR')}</div>`;

          for (const p of parcoursSorted) {
            html += `<h2>${p.poleNom ? `Pôle ${p.poleNom} — ` : ''}${p.nom}</h2>`;
            for (const cl of p.classes) {
              html += `<h3>${cl.code} — ${cl.optionNom} (${cl.niveau})</h3>`;
              html += `<table><thead><tr><th style="width:30px">N°</th><th style="width:90px">Code</th><th>Matière</th><th style="width:40px">S</th><th style="width:70px">UE</th><th style="width:180px">Formateur</th></tr></thead><tbody>`;
              cl.matieres.forEach((m, i) => {
                const isTc = allMatieres.find(am => am.id === m.id)?.option === 'Tronc Commun';
                const trClass = isTc ? ' class="tc"' : '';
                const fCell = m.formateur
                  ? m.formateur
                  : '<span class="no-formateur">Non attribué</span>';
                html += `<tr${trClass}><td style="text-align:center">${i + 1}</td><td>${m.code}</td><td>${m.nom}${isTc ? ' (TC)' : ''}</td><td style="text-align:center">${m.semestre ? 'S' + m.semestre : ''}</td><td>${m.ue_code || ''}</td><td>${fCell}</td></tr>`;
              });
              html += `</tbody></table>`;
            }
          }

          html += `</body></html>`;
          w.document.write(html);
          w.document.close();
          w.print();
        };

        // ── Planning par jours d'examen ──
        // Effectifs par classe depuis choix_formulaire
        const effectifsClasse: Record<string, number> = {};
        if (choixFormulaire?.classes) {
          for (const c of choixFormulaire.classes) {
            effectifsClasse[c.code] = c.count;
          }
        }

        // Construire le planning pour le niveau+semestre sélectionné
        type PlanningMatiere = { code: string; nom: string; formateur: string; isTc: boolean };
        type PlanningClasse = { code: string; optionNom: string; effectif: number; matieres: PlanningMatiere[] };
        type PlanningJour = { jour: number; classes: Array<{ code: string; optionNom: string; effectif: number; matiere: PlanningMatiere | null }> };

        const planningClasses: PlanningClasse[] = [];
        for (const cl of allClasses) {
          const key = `${cl.option_nom}|||${cl.niveau}`;
          const classMatieres = matsByOptionNiveau.get(key) || [];
          const tcKey = `Tronc Commun|||${cl.niveau}`;
          const tcMatieres = matsByOptionNiveau.get(tcKey) || [];

          // Filtrer par le semestre choisi et le niveau choisi
          const semMatieres = [...classMatieres, ...tcMatieres]
            .filter(m => m.semestre === planningSemestre)
            .sort((a, b) => a.nom.localeCompare(b.nom));

          if (cl.niveau === planningNiveau && semMatieres.length > 0) {
            planningClasses.push({
              code: cl.code,
              optionNom: cl.option_nom,
              effectif: effectifsClasse[cl.code] || 0,
              matieres: semMatieres.map(m => ({
                code: m.code,
                nom: m.nom,
                formateur: m.formateur,
                isTc: allMatieres.find(am => am.id === m.id)?.option === 'Tronc Commun',
              })),
            });
          }
        }

        // Nombre max de matières (= nombre de jours)
        const maxJours = Math.max(...planningClasses.map(c => c.matieres.length), 0);

        // Construire les jours
        const planningJours: PlanningJour[] = [];
        for (let j = 0; j < maxJours; j++) {
          planningJours.push({
            jour: j + 1,
            classes: planningClasses.map(cl => ({
              code: cl.code,
              optionNom: cl.optionNom,
              effectif: cl.effectif,
              matiere: cl.matieres[j] || null,
            })),
          });
        }

        // Calculer les semestres valides pour le niveau sélectionné
        const semestresForPlanning = getSemestresForNiveau(planningNiveau);

        const handlePrintPlanning = () => {
          const w = window.open('', '_blank');
          if (!w) return;
          let html = `<html><head><title>Planning Examens par Jours — ${planningNiveau} S${planningSemestre}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
  h1 { font-size: 16px; text-align: center; margin-bottom: 4px; }
  .sub { font-size: 10px; text-align: center; color: #666; margin-bottom: 15px; }
  h2 { font-size: 13px; color: #1a56db; margin: 16px 0 6px; border-bottom: 2px solid #1a56db; padding-bottom: 3px; }
  .summary { display: flex; gap: 20px; justify-content: center; margin-bottom: 15px; }
  .summary-card { background: #f3f4f6; border-radius: 6px; padding: 8px 16px; text-align: center; }
  .summary-card .value { font-size: 18px; font-weight: bold; color: #1a56db; }
  .summary-card .label { font-size: 9px; color: #666; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
  th { background: #2563eb; color: white; font-size: 10px; }
  .jour-header { background: #dbeafe; font-weight: bold; font-size: 12px; padding: 6px 8px; }
  .capacite-total { background: #fef3c7; font-weight: bold; }
  .tc { background: #f0fdf4; font-style: italic; }
  .no-formateur { color: #dc2626; font-style: italic; }
  @media print { body { margin: 10px; } }
  @page { size: landscape A4; margin: 10mm; }
</style></head><body>`;
          html += `<h1>Planning des Examens par Jours</h1>`;
          html += `<div class="sub">${planningNiveau} — Semestre S${planningSemestre} — ${anneeActive?.libelle || ''} — Généré le ${new Date().toLocaleDateString('fr-FR')}</div>`;

          const totalEtudiants = planningClasses.reduce((s, c) => s + c.effectif, 0);
          html += `<div class="summary">`;
          html += `<div class="summary-card"><div class="value">${planningClasses.length}</div><div class="label">Classes</div></div>`;
          html += `<div class="summary-card"><div class="value">${totalEtudiants}</div><div class="label">Étudiants</div></div>`;
          html += `<div class="summary-card"><div class="value">${maxJours}</div><div class="label">Jours d'examen</div></div>`;
          html += `</div>`;

          for (const jour of planningJours) {
            const jourEffectif = jour.classes.filter(c => c.matiere).reduce((s, c) => s + c.effectif, 0);
            html += `<h2>Jour ${jour.jour} — Capacité requise : ${jourEffectif} étudiants</h2>`;
            html += `<table><thead><tr><th style="width:80px">Classe</th><th>Option</th><th style="width:60px">Effectif</th><th>Matière</th><th style="width:160px">Formateur</th></tr></thead><tbody>`;
            for (const c of jour.classes) {
              if (!c.matiere) {
                html += `<tr><td>${c.code}</td><td>${c.optionNom}</td><td style="text-align:center">${c.effectif}</td><td colspan="2" style="color:#999;font-style:italic;text-align:center">— Pas de matière —</td></tr>`;
              } else {
                const trClass = c.matiere.isTc ? ' class="tc"' : '';
                const fCell = c.matiere.formateur || '<span class="no-formateur">Non attribué</span>';
                html += `<tr${trClass}><td>${c.code}</td><td>${c.optionNom}</td><td style="text-align:center">${c.effectif}</td><td>${c.matiere.nom}${c.matiere.isTc ? ' (TC)' : ''}</td><td>${fCell}</td></tr>`;
              }
            }
            html += `<tr class="capacite-total"><td colspan="2" style="text-align:right;font-weight:bold">Total du jour</td><td style="text-align:center;font-weight:bold">${jourEffectif}</td><td colspan="2"></td></tr>`;
            html += `</tbody></table>`;
          }

          html += `</body></html>`;
          w.document.write(html);
          w.document.close();
          w.print();
        };

        return (
          <div className="space-y-4">
            {/* Sub-tabs */}
            <div className="flex items-center gap-4 border-b border-gray-200 dark:border-gray-700 pb-2">
              <button
                onClick={() => setRecapView('parcours')}
                className={`text-sm font-medium pb-1 border-b-2 transition-colors ${
                  recapView === 'parcours'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Par Parcours
              </button>
              <button
                onClick={() => setRecapView('planning')}
                className={`text-sm font-medium pb-1 border-b-2 transition-colors flex items-center gap-1.5 ${
                  recapView === 'planning'
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Calendar size={14} />
                Planning par Jours
              </button>
            </div>

            {/* Vue Par Parcours */}
            {recapView === 'parcours' && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Liste des matières organisée par parcours et classe, avec les formateurs associés.
                  </p>
                  <Button variant="outline" size="sm" icon={<Printer size={16} />} onClick={handlePrintRecap}>
                    Imprimer
                  </Button>
                </div>

                {parcoursSorted.length === 0 ? (
                  <Card>
                    <div className="text-center py-12 text-gray-400">
                      <p>Aucune donnée à afficher. Vérifiez que des classes et matières sont configurées.</p>
                    </div>
                  </Card>
                ) : (
                  parcoursSorted.map((p) => (
                    <Card key={p.nom}>
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                          {p.poleNom && (
                            <Badge variant="info" className="text-xs">{p.poleNom}</Badge>
                          )}
                          {p.nom}
                        </h3>
                      </div>
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {p.classes.map((cl) => {
                          const nbAttribues = cl.matieres.filter(m => m.formateur).length;
                          const pctAttribues = cl.matieres.length > 0 ? Math.round((nbAttribues / cl.matieres.length) * 100) : 0;

                          return (
                            <div key={cl.code}>
                              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/30 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{cl.code}</span>
                                  <span className="text-xs text-gray-500">{cl.optionNom}</span>
                                  <Badge variant="default" className="text-[10px]">{cl.niveau}</Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-500">{cl.matieres.length} matières</span>
                                  <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${pctAttribues === 100 ? 'bg-green-500' : pctAttribues >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                      style={{ width: `${pctAttribues}%` }}
                                    />
                                  </div>
                                  <span className={`text-[10px] font-medium ${pctAttribues === 100 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                                    {pctAttribues}%
                                  </span>
                                </div>
                              </div>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-4 py-1.5 text-left w-8">#</th>
                                    <th className="px-2 py-1.5 text-left w-24">Code</th>
                                    <th className="px-2 py-1.5 text-left">Matière</th>
                                    <th className="px-2 py-1.5 text-center w-10">S</th>
                                    <th className="px-2 py-1.5 text-left w-20">UE</th>
                                    <th className="px-2 py-1.5 text-left w-48">Formateur</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                  {cl.matieres.map((m, i) => {
                                    const isTc = allMatieres.find(am => am.id === m.id)?.option === 'Tronc Commun';
                                    return (
                                      <tr
                                        key={m.id}
                                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${isTc ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}
                                      >
                                        <td className="px-4 py-1.5 text-xs text-gray-400">{i + 1}</td>
                                        <td className="px-2 py-1.5 font-mono text-xs text-gray-500">{m.code}</td>
                                        <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">
                                          {m.nom}
                                          {isTc && <span className="ml-1 text-[9px] text-green-600 dark:text-green-400">(TC)</span>}
                                        </td>
                                        <td className="px-2 py-1.5 text-center text-xs text-gray-500">{m.semestre ? `S${m.semestre}` : ''}</td>
                                        <td className="px-2 py-1.5 text-xs text-gray-500">{m.ue_code || ''}</td>
                                        <td className="px-2 py-1.5">
                                          {m.formateur ? (
                                            <span className="text-xs text-blue-700 dark:text-blue-400 font-medium">{m.formateur}</span>
                                          ) : (
                                            <span className="text-xs text-red-400 italic">Non attribué</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  ))
                )}
              </>
            )}

            {/* Vue Planning par Jours */}
            {recapView === 'planning' && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Niveau</label>
                      <select
                        value={planningNiveau}
                        onChange={e => {
                          setPlanningNiveau(e.target.value);
                          const sems = getSemestresForNiveau(e.target.value);
                          if (sems.length > 0) setPlanningSemestre(sems[0].value);
                        }}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        {NIVEAUX.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-0.5">Semestre</label>
                      <select
                        value={planningSemestre}
                        onChange={e => setPlanningSemestre(Number(e.target.value))}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      >
                        {semestresForPlanning.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" icon={<Printer size={16} />} onClick={handlePrintPlanning}>
                    Imprimer le planning
                  </Button>
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <div className="p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{planningClasses.length}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Classes concernées</div>
                    </div>
                  </Card>
                  <Card>
                    <div className="p-4 text-center">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {planningClasses.reduce((s, c) => s + c.effectif, 0)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Étudiants au total</div>
                    </div>
                  </Card>
                  <Card>
                    <div className="p-4 text-center">
                      <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{maxJours}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Jours d'examen</div>
                    </div>
                  </Card>
                </div>

                {planningJours.length === 0 ? (
                  <Card>
                    <div className="text-center py-12 text-gray-400">
                      <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                      <p>Aucune matière pour {planningNiveau} — S{planningSemestre}</p>
                    </div>
                  </Card>
                ) : (
                  planningJours.map(jour => {
                    const jourEffectif = jour.classes.filter(c => c.matiere).reduce((s, c) => s + c.effectif, 0);
                    return (
                      <Card key={jour.jour}>
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-amber-50 to-transparent dark:from-amber-900/10 flex items-center justify-between">
                          <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Calendar size={16} className="text-amber-600 dark:text-amber-400" />
                            Jour {jour.jour}
                          </h3>
                          <div className="flex items-center gap-2">
                            <Badge variant="warning" className="text-xs">
                              {jourEffectif} étudiants attendus
                            </Badge>
                            <Badge variant="default" className="text-xs">
                              {jour.classes.filter(c => c.matiere).length} classes
                            </Badge>
                          </div>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                              <th className="px-4 py-2 text-left w-20">Classe</th>
                              <th className="px-2 py-2 text-left">Option</th>
                              <th className="px-2 py-2 text-center w-16">Effectif</th>
                              <th className="px-2 py-2 text-left">Matière</th>
                              <th className="px-2 py-2 text-left w-44">Formateur</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                            {jour.classes.map(c => (
                              <tr key={c.code} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${c.matiere?.isTc ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`}>
                                <td className="px-4 py-2 font-semibold text-sm text-gray-800 dark:text-gray-200">{c.code}</td>
                                <td className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">{c.optionNom}</td>
                                <td className="px-2 py-2 text-center font-medium">{c.effectif || '—'}</td>
                                {c.matiere ? (
                                  <>
                                    <td className="px-2 py-2 text-gray-800 dark:text-gray-200">
                                      {c.matiere.nom}
                                      {c.matiere.isTc && <span className="ml-1 text-[9px] text-green-600 dark:text-green-400">(TC)</span>}
                                    </td>
                                    <td className="px-2 py-2">
                                      {c.matiere.formateur ? (
                                        <span className="text-xs text-blue-700 dark:text-blue-400 font-medium">{c.matiere.formateur}</span>
                                      ) : (
                                        <span className="text-xs text-red-400 italic">Non attribué</span>
                                      )}
                                    </td>
                                  </>
                                ) : (
                                  <td colSpan={2} className="px-2 py-2 text-center text-xs text-gray-400 italic">— Pas de matière —</td>
                                )}
                              </tr>
                            ))}
                            <tr className="bg-amber-50 dark:bg-amber-900/10 font-semibold">
                              <td colSpan={2} className="px-4 py-2 text-right text-xs text-gray-600 dark:text-gray-400">Total du jour</td>
                              <td className="px-2 py-2 text-center text-amber-700 dark:text-amber-400">{jourEffectif}</td>
                              <td colSpan={2}></td>
                            </tr>
                          </tbody>
                        </table>
                      </Card>
                    );
                  })
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Fix 2026-09-09 : ONGLET MATIÈRES DÉSACTIVÉES */}
      {activeTab === 'desactivees' && (
        <Card>
          <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Matières désactivées ({matieresDesactivees.length})
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Ces matières n'apparaissent plus dans la saisie, les relevés, les EDT, les stats
                ni l'audit crédits. Vous pouvez les réactiver ici pour les remettre en circulation.
              </p>
            </div>
          </div>
          {matieresLoading ? (
            <div className="py-8 text-center text-gray-500 text-sm">Chargement…</div>
          ) : matieresDesactivees.length === 0 ? (
            <div className="py-10 text-center text-gray-500 dark:text-gray-400">
              <div className="text-sm">Aucune matière désactivée.</div>
              <div className="text-xs mt-1">Toutes les matières sont actives.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="text-left px-3 py-2">Code</th>
                    <th className="text-left px-3 py-2">Nom</th>
                    <th className="text-center px-3 py-2">Niveau</th>
                    <th className="text-center px-3 py-2">Sem</th>
                    <th className="text-left px-3 py-2">Option (UE)</th>
                    <th className="text-center px-3 py-2">Crédits</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {matieresDesactivees.map((m: any) => (
                    <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{m.code_matiere}</td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">{m.nom_matiere}</td>
                      <td className="px-3 py-2 text-center text-xs">{m.niveau}</td>
                      <td className="px-3 py-2 text-center text-xs">S{m.semestre}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                        {(m as any).ue_option || (m as any).option || <span className="italic text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-center font-mono">{m.credits}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!window.confirm(
                                `Réactiver « ${m.nom_matiere} » (${m.code_matiere}) ?\n\n` +
                                `Elle réapparaîtra dans la saisie, les relevés, les EDT, ` +
                                `les stats et l'audit crédits.`
                              )) return;
                              try {
                                await updateMatiere({
                                  id: m.id,
                                  matiere: { actif: true } as any,
                                }).unwrap();
                                refetchMatieres();
                              } catch (e: any) {
                                alert(e?.data?.detail || 'Erreur réactivation');
                              }
                            }}
                            className="text-emerald-600 dark:text-emerald-400"
                            title="Réactiver la matière"
                          >
                            Réactiver
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              handleEditMatiere(m);
                              setActiveTab('matieres');
                            }}
                            className="text-blue-600 dark:text-blue-400"
                            title="Modifier"
                          >
                            Modifier
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* UES TAB */}
      {activeTab === 'ues' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              variant="primary"
              icon={<Plus size={18} />}
              onClick={() => {
                resetUEForm();
                setShowUEModal(true);
              }}
            >
              Nouvelle UE
            </Button>
          </div>

          <div className="space-y-3">
            {filteredUEs.length === 0 ? (
              <Card>
                <div className="py-8 text-center text-gray-500 dark:text-gray-400">Aucune UE trouvee</div>
              </Card>
            ) : (
              filteredUEs.map((ue) => {
                const ueMatieres = getMatieresForUE(ue.id);
                const isExpanded = expandedUEs.has(ue.id);

                return (
                  <Card key={ue.id}>
                    <div className="flex items-center justify-between">
                      <div
                        className="flex items-center space-x-3 cursor-pointer flex-1"
                        onClick={() => toggleUEExpansion(ue.id)}
                      >
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                            {ue.code_ue} - {ue.nom_ue}
                          </h3>
                          <div className="flex space-x-2 mt-1">
                            <Badge variant="info">{ue.niveau}</Badge>
                            <Badge variant="default">S{ue.semestre}</Badge>
                            <Badge variant="default">{ue.option}</Badge>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{ue.nb_matieres} matiere(s)</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" icon={<Edit size={14} />} onClick={() => handleEditUE(ue)} />
                        <Button
                          variant="danger"
                          size="sm"
                          icon={<Trash2 size={14} />}
                          onClick={() => handleDeleteUE(ue.id)}
                        />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {ueMatieres.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 italic">Aucune matiere dans cette UE</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Code</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Nom</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Credits</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Coef. Devoir</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Coef. Examen</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-600">
                              {ueMatieres.map((m) => (
                                <tr key={m.id}>
                                  <td className="px-3 py-2 font-medium">{m.code_matiere}</td>
                                  <td className="px-3 py-2">{m.nom_matiere}</td>
                                  <td className="px-3 py-2 text-center">{m.credits}</td>
                                  <td className="px-3 py-2 text-center">{m.coefficient_devoir}</td>
                                  <td className="px-3 py-2 text-center">{m.coefficient_examen}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* MATIERE MODAL */}
      {showMatiereModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              {editingMatiere ? 'Modifier la Matiere' : 'Nouvelle Matiere'}
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Code <span className="text-xs text-gray-400">(auto-généré si vide)</span>
                  </label>
                  <Input
                    value={matiereForm.code_matiere}
                    onChange={(e) => setMatiereForm({ ...matiereForm, code_matiere: e.target.value })}
                    placeholder="Auto-généré si vide"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Crédits *</label>
                  <Input
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.5"
                    value={matiereForm.credits}
                    onChange={(e) => setMatiereForm({ ...matiereForm, credits: parseFloat(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom *</label>
                <Input
                  value={matiereForm.nom_matiere}
                  onChange={(e) => setMatiereForm({ ...matiereForm, nom_matiere: e.target.value })}
                  placeholder="Ex: Mathématiques Appliquées"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type de formation
                  </label>
                  <select
                    value={matiereFormFormation}
                    onChange={(e) => {
                      setMatiereFormFormation(e.target.value as '' | 'FI' | 'CS');
                      setMatiereFormOption('');
                      setMatiereForm({ ...matiereForm, ue: '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">FI + CS (toutes)</option>
                    <option value="FI">FI (Formation Initiale)</option>
                    <option value="CS">CS (Formation Continue)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Option <span className="text-xs text-gray-400">(filtre les UE)</span>
                  </label>
                  <select
                    value={matiereFormOption}
                    onChange={(e) => {
                      setMatiereFormOption(e.target.value);
                      setMatiereForm({ ...matiereForm, ue: '' });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Toutes les options</option>
                    {optionsForMatiereForm.map((o: any) => {
                      const isCS = (o.code || '').endsWith('-CS');
                      return (
                        <option key={o.id} value={o.nom}>
                          {isCS ? '🌙 ' : ''}{o.code} — {o.nom}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unité d'Enseignement <span className="text-xs text-gray-400">(optionnel)</span>
                </label>
                <select
                  value={matiereForm.ue}
                  onChange={(e) => {
                    const ueId = e.target.value;
                    const selectedUE = ueId ? ues?.find((u) => u.id.toString() === ueId) : null;
                    setMatiereForm({
                      ...matiereForm,
                      ue: ueId,
                      ...(selectedUE ? { niveau: selectedUE.niveau, semestre: selectedUE.semestre } : {}),
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">-- Sans UE (assignable plus tard) --</option>
                  {uesForMatiereForm.map((ue) => {
                    const isCS = (ue.code_ue || '').endsWith('-CS')
                      || (ue.option && optionsByNom[ue.option]?.code?.endsWith('-CS'));
                    return (
                      <option key={ue.id} value={ue.id}>
                        {isCS ? '🌙 ' : ''}{ue.code_ue} - {ue.nom_ue} ({ue.niveau} S{ue.semestre} - {ue.option})
                      </option>
                    );
                  })}
                </select>
                {uesForMatiereForm.length === 0 && matiereFormFormation === 'CS' && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Aucune UE de Formation Continue trouvée. Vous pouvez créer l'option CS via le bouton "Dupliquer pour CS" dans Gestion Options.
                  </p>
                )}
              </div>

              {/* Niveau et Semestre : affichés seulement si pas d'UE sélectionnée */}
              {!matiereForm.ue && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau *</label>
                    <select
                      value={matiereForm.niveau}
                      onChange={(e) => {
                        const newNiveau = e.target.value;
                        const semestres = getSemestresForNiveau(newNiveau);
                        setMatiereForm({
                          ...matiereForm,
                          niveau: newNiveau,
                          semestre: semestres[0]?.value || 1,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {NIVEAUX.map((n) => (
                        <option key={n.value} value={n.value}>{n.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre *</label>
                    <select
                      value={matiereForm.semestre}
                      onChange={(e) => setMatiereForm({ ...matiereForm, semestre: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {getSemestresForNiveau(matiereForm.niveau).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {matiereForm.ue && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    Niveau et semestre dérivés de l'UE sélectionnée
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coef. Devoir</label>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={matiereForm.coefficient_devoir}
                    onChange={(e) =>
                      setMatiereForm({ ...matiereForm, coefficient_devoir: parseFloat(e.target.value) })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coef. Examen</label>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={matiereForm.coefficient_examen}
                    onChange={(e) =>
                      setMatiereForm({ ...matiereForm, coefficient_examen: parseFloat(e.target.value) })
                    }
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                * La somme des coefficients doit être égale à 1
              </p>
            </div>

            <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 mt-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex justify-end space-x-3 rounded-b-lg">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMatiereModal(false);
                  resetMatiereForm();
                }}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateOrUpdateMatiere}
                disabled={createMatiereLoading || updateMatiereLoading}
              >
                {createMatiereLoading || updateMatiereLoading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* UE MODAL */}
      {showUEModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">{editingUE ? 'Modifier l\'UE' : 'Nouvelle UE'}</h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code UE *</label>
                  <Input
                    value={ueForm.code_ue}
                    onChange={(e) => setUeForm({ ...ueForm, code_ue: e.target.value })}
                    placeholder="Ex: UE1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau *</label>
                  <select
                    value={ueForm.niveau}
                    onChange={(e) => {
                      const newNiveau = e.target.value;
                      const semestres = getSemestresForNiveau(newNiveau);
                      setUeForm({
                        ...ueForm,
                        niveau: newNiveau,
                        semestre: semestres[0]?.value || 1,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {NIVEAUX.map((n) => (
                      <option key={n.value} value={n.value}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom UE *</label>
                <Input
                  value={ueForm.nom_ue}
                  onChange={(e) => setUeForm({ ...ueForm, nom_ue: e.target.value })}
                  placeholder="Ex: Methodes & Outils Scientifiques"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre *</label>
                  <select
                    value={ueForm.semestre}
                    onChange={(e) => setUeForm({ ...ueForm, semestre: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {getSemestresForNiveau(ueForm.niveau).map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Option *</label>
                  <select
                    value={ueForm.option}
                    onChange={(e) => setUeForm({ ...ueForm, option: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">-- Selectionner --</option>
                    {options?.map((o) => (
                      <option key={o.id} value={o.code}>
                        {o.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowUEModal(false);
                  resetUEForm();
                }}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateOrUpdateUE}
                disabled={createUELoading || updateUELoading}
              >
                {createUELoading || updateUELoading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* COPY MODAL */}
      {showCopyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Copier les UE/Matières vers une autre option
              </h2>
              <button onClick={() => setShowCopyModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Copiez toutes les UE et matières d'une option vers une ou plusieurs autres options du même niveau.
              Les UE déjà existantes dans l'option cible seront ignorées.
            </p>

            <div className="space-y-4">
              {/* Niveau */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
                <select
                  value={copyForm.niveau}
                  onChange={(e) => setCopyForm({ ...copyForm, niveau: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {NIVEAUX.map((n) => (
                    <option key={n.value} value={n.value}>{n.label}</option>
                  ))}
                </select>
              </div>

              {/* Option source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Option source</label>
                <select
                  value={copyForm.option_source}
                  onChange={(e) => setCopyForm({ ...copyForm, option_source: e.target.value, options_cibles: [] })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">-- Sélectionner l'option source --</option>
                  {options?.map((o) => (
                    <option key={o.id} value={o.nom}>{o.nom}</option>
                  ))}
                </select>
              </div>

              {/* Options cibles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Options cibles (sélectionner une ou plusieurs)
                </label>
                <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2 space-y-1">
                  {(() => {
                    const cibles = options?.filter((o) => o.nom !== copyForm.option_source) ?? [];
                    // Grouper par parcours
                    const avecParcours = cibles.filter((o) => o.parcours_nom);
                    const sansParcours = cibles.filter((o) => !o.parcours_nom);
                    const groupes = new Map<string, typeof cibles>();
                    avecParcours.forEach((o) => {
                      const key = o.parcours_nom!;
                      if (!groupes.has(key)) groupes.set(key, []);
                      groupes.get(key)!.push(o);
                    });

                    return (
                      <>
                        {Array.from(groupes.entries()).map(([parcNom, opts]) => (
                          <div key={parcNom}>
                            <p className="px-2 py-1 text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded">
                              {parcNom}
                            </p>
                            {opts.map((o) => (
                              <label
                                key={o.id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                  copyForm.options_cibles.includes(o.nom)
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={copyForm.options_cibles.includes(o.nom)}
                                  onChange={() => toggleCibleOption(o.nom)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-gray-100">{o.nom}</span>
                                {copyForm.options_cibles.includes(o.nom) && (
                                  <CheckCircle size={14} className="text-blue-600 ml-auto" />
                                )}
                              </label>
                            ))}
                          </div>
                        ))}
                        {sansParcours.length > 0 && (
                          <div>
                            {groupes.size > 0 && (
                              <p className="px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded">
                                Autres
                              </p>
                            )}
                            {sansParcours.map((o) => (
                              <label
                                key={o.id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                  copyForm.options_cibles.includes(o.nom)
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={copyForm.options_cibles.includes(o.nom)}
                                  onChange={() => toggleCibleOption(o.nom)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-gray-100">{o.nom}</span>
                                {copyForm.options_cibles.includes(o.nom) && (
                                  <CheckCircle size={14} className="text-blue-600 ml-auto" />
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                {copyForm.options_cibles.length > 0 && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    {copyForm.options_cibles.length} option(s) sélectionnée(s)
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowCopyModal(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                icon={<Copy size={16} />}
                onClick={handleCopierUEs}
                disabled={copierLoading || !copyForm.option_source || copyForm.options_cibles.length === 0}
              >
                {copierLoading ? 'Copie en cours...' : 'Copier'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* CLEANUP MODAL */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trash size={20} className="text-red-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Nettoyage des UE / Matières
                </h2>
              </div>
              <button
                onClick={() => { setShowCleanupModal(false); setCleanupStep(1); setCleanupApercu(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-5 text-xs text-gray-500 dark:text-gray-400">
              <span className={`px-2 py-0.5 rounded-full font-medium ${cleanupStep === 1 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 dark:bg-gray-700'}`}>
                1. Sélection
              </span>
              <span>→</span>
              <span className={`px-2 py-0.5 rounded-full font-medium ${cleanupStep === 2 ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 dark:bg-gray-700'}`}>
                2. Aperçu &amp; Confirmation
              </span>
            </div>

            {/* STEP 1 — Sélection du niveau + scope */}
            {cleanupStep === 1 && (
              <div className="space-y-4">
                {/* Niveau (obligatoire) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    1. Sélectionner le niveau
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {NIVEAUX.map((n) => (
                      <button
                        key={n.value}
                        onClick={() => setCleanupNiveau(n.value)}
                        className={`p-3 rounded-lg border-2 text-center transition-colors font-medium ${
                          cleanupNiveau === n.value
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {n.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scope */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    2. Supprimer les matières de
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {([
                      { value: 'tout' as const, label: 'Tout le niveau', desc: `Toutes les ${cleanupNiveau}` },
                      { value: 'option' as const, label: 'Une option', desc: 'Option précise' },
                      { value: 'options' as const, label: 'Plusieurs options', desc: 'Sélection multiple' },
                      { value: 'parcours' as const, label: 'Un parcours', desc: 'Tout un parcours' },
                      { value: 'pole' as const, label: 'Un pôle', desc: 'Tout un pôle' },
                    ]).map((s) => (
                      <button
                        key={s.value}
                        onClick={() => { setCleanupScope(s.value); setCleanupOption(''); setCleanupSelectedOptions([]); setCleanupPole(''); setCleanupParcours(''); }}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          cleanupScope === s.value
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{s.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{s.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sélection détaillée selon le scope */}
                {cleanupScope === 'option' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Option à nettoyer</label>
                    <select
                      value={cleanupOption}
                      onChange={(e) => setCleanupOption(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">-- Sélectionner une option --</option>
                      {options?.map((o) => (
                        <option key={o.id} value={o.nom}>{o.code} — {o.nom}</option>
                      ))}
                    </select>
                  </div>
                )}

                {cleanupScope === 'options' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Options à nettoyer</label>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                      {options?.map((o) => (
                        <label key={o.id} className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={cleanupSelectedOptions.includes(o.nom)}
                            onChange={() => {
                              setCleanupSelectedOptions((prev) =>
                                prev.includes(o.nom) ? prev.filter((c) => c !== o.nom) : [...prev, o.nom]
                              );
                            }}
                            className="mr-3 h-4 w-4 text-red-600 rounded border-gray-300 dark:border-gray-600 focus:ring-red-500"
                          />
                          <span className="text-sm text-gray-900 dark:text-gray-100">{o.code} — {o.nom}</span>
                        </label>
                      ))}
                    </div>
                    {cleanupSelectedOptions.length > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {cleanupSelectedOptions.length} option(s) sélectionnée(s)
                      </p>
                    )}
                  </div>
                )}

                {cleanupScope === 'parcours' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Parcours à nettoyer</label>
                    <select
                      value={cleanupParcours}
                      onChange={(e) => setCleanupParcours(e.target.value ? parseInt(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">-- Sélectionner un parcours --</option>
                      {parcoursList?.map((p) => (
                        <option key={p.id} value={p.id}>{p.nom} ({p.pole_nom})</option>
                      ))}
                    </select>
                    {cleanupParcours !== '' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {options?.filter((o) => o.parcours === cleanupParcours).length ?? 0} option(s) dans ce parcours
                      </p>
                    )}
                  </div>
                )}

                {cleanupScope === 'pole' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pôle à nettoyer</label>
                    <select
                      value={cleanupPole}
                      onChange={(e) => setCleanupPole(e.target.value ? parseInt(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">-- Sélectionner un pôle --</option>
                      {poles?.map((p) => (
                        <option key={p.id} value={p.id}>{p.nom}</option>
                      ))}
                    </select>
                    {cleanupPole !== '' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {options?.filter((o) => o.pole === cleanupPole).length ?? 0} option(s) dans ce pôle
                      </p>
                    )}
                  </div>
                )}

                {/* Résumé de la sélection */}
                {cleanupScope === 'tout' && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      Toutes les UE et matières du niveau <strong>{cleanupNiveau}</strong> seront supprimées, toutes options confondues.
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="outline" onClick={() => { setShowCleanupModal(false); setCleanupApercu(null); }}>
                    Annuler
                  </Button>
                  <Button
                    variant="primary"
                    icon={apercuLoading ? undefined : <Eye size={16} />}
                    onClick={handleApercuNettoyage}
                    disabled={
                      apercuLoading ||
                      !cleanupNiveau ||
                      (cleanupScope === 'option' && !cleanupOption) ||
                      (cleanupScope === 'options' && cleanupSelectedOptions.length === 0) ||
                      (cleanupScope === 'parcours' && cleanupParcours === '') ||
                      (cleanupScope === 'pole' && cleanupPole === '')
                    }
                  >
                    {apercuLoading ? 'Chargement...' : 'Voir l\'aperçu'}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2 — Aperçu + Confirmation */}
            {cleanupStep === 2 && cleanupApercu && (
              <div className="space-y-4">
                {/* Résumé */}
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-800 dark:text-red-300">
                        Cette action supprimera définitivement :
                      </p>
                      <ul className="mt-1 text-sm text-red-700 dark:text-red-400 space-y-0.5">
                        {cleanupApercu.nb_ues > 0 && (
                          <li>• <strong>{cleanupApercu.nb_ues}</strong> Unité(s) d'Enseignement</li>
                        )}
                        <li>• <strong>{cleanupApercu.nb_matieres}</strong> Matière(s)
                          {cleanupApercu.nb_orphelines && cleanupApercu.nb_orphelines > 0 && (
                            <span> (dont <strong>{cleanupApercu.nb_orphelines}</strong> sans UE)</span>
                          )}
                        </li>
                      </ul>

                      {/* Données dépendantes */}
                      {cleanupApercu.dependances && Object.keys(cleanupApercu.dependances).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-700">
                          <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                            Les données suivantes, liées à ces matières, seront également supprimées :
                          </p>
                          <ul className="text-sm text-red-700 dark:text-red-400 space-y-0.5">
                            {Object.entries(cleanupApercu.dependances).map(([label, count]) => (
                              <li key={label}>• <strong>{count}</strong> {label}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Liste des UEs concernées */}
                {cleanupApercu.backup.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      UE concernées :
                    </p>
                    <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                      {cleanupApercu.backup.map((ue, i) => (
                        <div key={i} className="px-3 py-2 flex items-center justify-between">
                          <div>
                            <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{ue.code_ue}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{ue.nom_ue}</span>
                          </div>
                          <div className="flex gap-1">
                            <Badge variant="info">{ue.niveau}</Badge>
                            <Badge variant="default">{ue.option}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Backup */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Télécharger le backup avant suppression</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">Fichier JSON contenant toutes les UE et matières</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<Download size={14} />}
                    onClick={handleTelechargerBackup}
                  >
                    Backup JSON
                  </Button>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="outline" onClick={() => setCleanupStep(1)}>
                    ← Retour
                  </Button>
                  <Button
                    variant="danger"
                    icon={<Trash size={16} />}
                    onClick={handleExecuterNettoyage}
                    disabled={nettoyageLoading || (cleanupApercu.nb_ues === 0 && cleanupApercu.nb_matieres === 0)}
                  >
                    {nettoyageLoading ? 'Suppression...' : `Supprimer ${cleanupApercu.nb_matieres} matière(s)${cleanupApercu.nb_ues > 0 ? ` et ${cleanupApercu.nb_ues} UE(s)` : ''}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BATCH CREATE MODAL */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Insertion en lot de matières
              </h2>
              <button onClick={() => setShowBatchModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Saisissez les noms des matières séparés par des points-virgules (;). Les crédits seront distribués automatiquement pour un total de 30.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau *</label>
                  <select
                    value={batchForm.niveau}
                    onChange={(e) => {
                      const newNiveau = e.target.value;
                      const semestres = getSemestresForNiveau(newNiveau);
                      setBatchForm({
                        ...batchForm,
                        niveau: newNiveau,
                        semestre: semestres[0]?.value || 1,
                        selectedOptionIds: [],
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {NIVEAUX.map((n) => (
                      <option key={n.value} value={n.value}>{n.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre *</label>
                  <select
                    value={batchForm.semestre}
                    onChange={(e) => setBatchForm({ ...batchForm, semestre: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {getSemestresForNiveau(batchForm.niveau).map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Filtre type de formation — évite d'insérer par erreur des matières CS dans des options FI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type de formation
                </label>
                <div className="flex items-center gap-2">
                  {([
                    { k: '', l: 'Les deux (FI + CS)', color: 'blue' },
                    { k: 'FI', l: 'FI seulement', color: 'gray' },
                    { k: 'CS', l: '🌙 CS seulement', color: 'amber' },
                  ] as const).map((opt) => {
                    const active = batchFormationFilter === opt.k;
                    return (
                      <button
                        key={opt.k || 'all'}
                        type="button"
                        onClick={() => {
                          setBatchFormationFilter(opt.k);
                          // Nettoyage : retirer les options qui ne matchent plus le filtre
                          if (opt.k) {
                            const allOptions = options || [];
                            const csIds = new Set(
                              allOptions.filter((o: any) => (o.code || '').endsWith('-CS')).map((o: any) => o.id),
                            );
                            setBatchForm((prev) => ({
                              ...prev,
                              selectedOptionIds: prev.selectedOptionIds.filter((id) =>
                                opt.k === 'CS' ? csIds.has(id) : !csIds.has(id),
                              ),
                            }));
                          }
                        }}
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium border transition ${
                          active
                            ? opt.k === 'CS'
                              ? 'bg-amber-500 text-white border-amber-500'
                              : opt.k === 'FI'
                              ? 'bg-gray-600 text-white border-gray-600'
                              : 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {opt.l}
                      </button>
                    );
                  })}
                </div>
                {batchFormationFilter === 'CS' && (
                  <div className="mt-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                    ⚠ Mode CS uniquement — seules les options finissant par « -CS » sont sélectionnables. Aucune matière ne sera ajoutée aux options FI.
                  </div>
                )}
              </div>

              {/* Sélection des options — accordéon par parcours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Classes concernées <span className="text-gray-400 font-normal">(optionnel)</span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Sélectionnez les classes auxquelles associer ces matières. Les cours du soir (CS) sont séparés en bas.
                </p>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto">
                  {(() => {
                    const activeOptions = options || [];
                    const classesList = classes || [];

                    // Détection CS : par code option (-CS) OU code classe (-CS / CS-)
                    const isClasseCS = (code: string) => code.includes('-CS') || code.includes('CS-');
                    const isOptionCS = (opt: any) => (opt.code || '').endsWith('-CS');

                    // Options CS = celles dont le code se termine par -CS
                    // (créées via "Dupliquer pour CS"). Une option FI qui a des
                    // classes CS orphelines (ex: L2DI&GL + L2DI&GL-CS partageant
                    // la même option) reste FI — sinon toute l'option disparaît
                    // du bloc FI et les classes non-CS deviennent invisibles.
                    const optionsCSSet = new Set<number>();
                    activeOptions.forEach((opt) => {
                      if (isOptionCS(opt)) optionsCSSet.add(opt.id);
                    });

                    // Grouper les options FI par parcours (les CS sont à part)
                    const grouped: Record<string, { key: string; options: typeof activeOptions }> = {};
                    activeOptions.forEach((opt) => {
                      if (optionsCSSet.has(opt.id)) return; // exclure CS du bloc FI
                      const group = opt.parcours_nom
                        ? `${opt.pole_nom} / ${opt.parcours_nom}`
                        : opt.pole_nom;
                      if (!grouped[group]) grouped[group] = { key: group, options: [] };
                      grouped[group].options.push(opt);
                    });

                    if (activeOptions.length === 0) {
                      return (
                        <p className="text-sm text-gray-400 dark:text-gray-500 p-3 text-center">
                          Aucune option active disponible
                        </p>
                      );
                    }

                    // Helper : rendre une option avec ses classes
                    const renderOption = (opt: typeof activeOptions[0], showOnlyCS: boolean) => {
                      const classesForOption = classesList.filter(
                        (c) => c.option_nom === opt.nom && c.niveau === batchForm.niveau
                          && (showOnlyCS ? isClasseCS(c.code) : !isClasseCS(c.code))
                      );
                      // Ne pas afficher si aucune classe dans ce mode
                      if (classesForOption.length === 0 && showOnlyCS) return null;

                      const isSelected = batchForm.selectedOptionIds.includes(opt.id);

                      return (
                        <label
                          key={`${showOnlyCS ? 'cs-' : ''}${opt.id}`}
                          className={`flex items-center px-4 py-2 cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-600/50 transition-colors ${
                            isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              setBatchForm((prev) => ({
                                ...prev,
                                selectedOptionIds: isSelected
                                  ? prev.selectedOptionIds.filter((id) => id !== opt.id)
                                  : [...prev.selectedOptionIds, opt.id],
                              }));
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                          />
                          <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                            {opt.nom}
                          </span>
                          <div className="ml-auto flex gap-1 flex-shrink-0">
                            {classesForOption.length > 0 ? classesForOption.map((cl) => (
                              <span
                                key={cl.id}
                                className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                                  isClasseCS(cl.code)
                                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                    : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                }`}
                              >
                                {cl.code}
                              </span>
                            )) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                                pas de classe {batchForm.niveau}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    };

                    // Collecter les options CS pour la section séparée
                    const csEntries: typeof activeOptions = activeOptions.filter((o) => optionsCSSet.has(o.id));

                    return (
                      <>
                        {/* Formation Initiale — par parcours (masqué si filtre CS uniquement) */}
                        {batchFormationFilter !== 'CS' && Object.entries(grouped).map(([groupName, group]) => {
                          const selectedInGroup = group.options.filter((o) => batchForm.selectedOptionIds.includes(o.id)).length;
                          const isExpanded = batchExpandedGroups.includes(groupName);

                          return (
                            <div key={groupName} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                              <button
                                type="button"
                                onClick={() => {
                                  setBatchExpandedGroups((prev) =>
                                    prev.includes(groupName) ? prev.filter((g) => g !== groupName) : [...prev, groupName]
                                  );
                                }}
                                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  {isExpanded ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
                                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{groupName}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 dark:text-gray-500">{group.options.length} option(s)</span>
                                  {selectedInGroup > 0 && (
                                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-medium">
                                      {selectedInGroup}
                                    </span>
                                  )}
                                </div>
                              </button>
                              {isExpanded && (
                                <div>
                                  {group.options.map((opt) => renderOption(opt, false))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Formation Continue (CS) — section séparée (masqué si filtre FI uniquement) */}
                        {batchFormationFilter !== 'FI' && csEntries.length > 0 && (() => {
                          const csGroupName = '🌙 Formation Continue (Cours du soir)';
                          const isCSExpanded = batchExpandedGroups.includes(csGroupName);
                          const selectedCS = csEntries.filter((o) => batchForm.selectedOptionIds.includes(o.id)).length;

                          return (
                            <div className="border-t-2 border-amber-300 dark:border-amber-600">
                              <button
                                type="button"
                                onClick={() => {
                                  setBatchExpandedGroups((prev) =>
                                    prev.includes(csGroupName) ? prev.filter((g) => g !== csGroupName) : [...prev, csGroupName]
                                  );
                                }}
                                className="w-full flex items-center justify-between px-3 py-2 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  {isCSExpanded ? <ChevronDown size={14} className="text-amber-600" /> : <ChevronRight size={14} className="text-amber-600" />}
                                  <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">Formation Continue (Cours du soir)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-amber-500 dark:text-amber-400">{csEntries.length} option(s)</span>
                                  {selectedCS > 0 && (
                                    <span className="text-xs bg-amber-200 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200 px-1.5 py-0.5 rounded-full font-medium">
                                      {selectedCS}
                                    </span>
                                  )}
                                </div>
                              </button>
                              {isCSExpanded && (
                                <div className="bg-amber-50/30 dark:bg-amber-900/10">
                                  {csEntries.map((opt) => renderOption(opt, true))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
                {batchForm.selectedOptionIds.length > 0 && (
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      {batchForm.selectedOptionIds.length} option(s) sélectionnée(s)
                    </span>
                    <button
                      type="button"
                      onClick={() => setBatchForm((prev) => ({ ...prev, selectedOptionIds: [] }))}
                      className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Noms des matières (séparés par ;)
                </label>
                <textarea
                  value={batchForm.noms}
                  onChange={(e) => setBatchForm({ ...batchForm, noms: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows={4}
                  placeholder="Mathématiques; Physique; Informatique; Anglais; Chimie"
                />
              </div>

              {/* Aperçu live */}
              {(() => {
                const noms = batchForm.noms.split(';').map(n => n.trim()).filter(n => n);
                if (noms.length === 0) return null;

                // Distribution des crédits côté client (aperçu)
                const n = noms.length;
                const step = 0.5;
                const total = 30;
                const base = Math.floor((total / n) / step) * step;
                const credits = Array(n).fill(Math.max(base, 0.5));
                let remainder = total - credits.reduce((a: number, b: number) => a + b, 0);
                let idx = 0;
                while (remainder >= step && idx < n * 100) {
                  if (credits[idx % n] < 10) {
                    credits[idx % n] += step;
                    remainder -= step;
                  }
                  idx++;
                }

                const totalCredits = credits.reduce((a: number, b: number) => a + b, 0);
                const nbOptions = batchForm.selectedOptionIds.length;

                return (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Aperçu ({noms.length} matière{noms.length > 1 ? 's' : ''}
                      {nbOptions > 0 ? ` x ${nbOptions} option${nbOptions > 1 ? 's' : ''} = ${noms.length * nbOptions} au total` : ''})
                    </h4>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {noms.map((nom, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-1">
                          <span className="text-gray-900 dark:text-gray-100">
                            {i + 1}. {nom}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 font-medium">
                            {credits[i]?.toFixed(1)} cr.
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 flex justify-between text-sm font-semibold">
                      <span className="text-gray-700 dark:text-gray-300">Total crédits</span>
                      <span className={totalCredits === 30 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {totalCredits.toFixed(1)} / 30
                      </span>
                    </div>
                    {n > 60 && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        Trop de matières (max 60 pour 30 crédits avec min 0.5 par matière)
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowBatchModal(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                icon={<Plus size={16} />}
                onClick={async () => {
                  const noms = batchForm.noms.split(';').map(n => n.trim()).filter(n => n);
                  if (noms.length === 0) {
                    setMessage({ type: 'error', text: 'Saisissez au moins un nom de matière' });
                    return;
                  }
                  try {
                    const result = await batchCreate({
                      noms: batchForm.noms,
                      niveau: batchForm.niveau,
                      semestre: batchForm.semestre,
                      ...(batchForm.selectedOptionIds.length > 0 && {
                        option_ids: batchForm.selectedOptionIds,
                      }),
                    }).unwrap();
                    setMessage({ type: 'success', text: result.message });
                    setShowBatchModal(false);
                    setBatchForm({ noms: '', niveau: 'L1', semestre: 1, selectedOptionIds: [] });
                    refetchUEs();
                  } catch (error: any) {
                    setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors de la création en lot' });
                  }
                }}
                disabled={batchCreateLoading || !batchForm.noms.trim()}
              >
                {batchCreateLoading
                  ? 'Création en cours...'
                  : `Créer ${batchForm.noms.split(';').filter(n => n.trim()).length} matière(s)${
                      batchForm.selectedOptionIds.length > 0
                        ? ` pour ${batchForm.selectedOptionIds.length} option(s)`
                        : ''
                    }`
                }
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Modal impression par pôle, niveau et option */}
      {showPrintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Imprimer la liste des matières</h3>
              <button onClick={() => setShowPrintModal(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Sélection du pôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pôle</label>
                <select
                  value={printPole}
                  onChange={(e) => { setPrintPole(e.target.value ? Number(e.target.value) : ''); setPrintOption(''); }}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                >
                  <option value="">Tous les pôles</option>
                  {(poles || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </div>

              {/* Sélection du niveau */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPrintNiveau('')}
                    className={`px-3 py-2 text-sm rounded-md border ${!printNiveau ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    Tous
                  </button>
                  {NIVEAUX.map((n) => (
                    <button
                      key={n.value}
                      onClick={() => setPrintNiveau(n.value)}
                      className={`px-3 py-2 text-sm rounded-md border ${printNiveau === n.value ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                      {n.value}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sélection de l'option (classe) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Option / Classe <span className="text-xs text-gray-400 font-normal">(optionnel)</span>
                </label>
                <select
                  value={printOption}
                  onChange={(e) => setPrintOption(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                >
                  <option value="">Toutes les options</option>
                  {(() => {
                    // Filtrer les options selon le pôle sélectionné
                    let optionsFiltered = (options || []).filter(o => o.actif !== false);
                    if (printPole) {
                      optionsFiltered = optionsFiltered.filter(o => o.pole === printPole);
                    }
                    return optionsFiltered.map(o => (
                      <option key={o.id} value={o.nom}>{o.code} — {o.nom}</option>
                    ));
                  })()}
                </select>
                {printNiveau && printOption && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Classe : {printNiveau}{(options || []).find(o => o.nom === printOption)?.code || ''} — {printOption}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowPrintModal(false)}>Annuler</Button>
              <Button
                icon={<Printer size={16} />}
                onClick={() => {
                  handlePrintMatieresFormateurs(printPole, printNiveau, printOption);
                  setShowPrintModal(false);
                }}
              >
                Imprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal copie matières vers option */}
      {showCopyMatieresModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Copier {selectedMatieres.size} matière(s) vers une option
              </h3>
              <button onClick={() => setShowCopyMatieresModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Résumé des matières sélectionnées */}
              <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
                  Matières sélectionnées :
                </p>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  {filteredMatieres
                    .filter((m) => selectedMatieres.has(m.id))
                    .map((m) => (
                      <span key={m.id} className="text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200">
                        {m.nom_matiere}
                      </span>
                    ))
                  }
                </div>
              </div>

              {/* Options cibles */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Options cibles :
                </label>
                <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2 space-y-1">
                  {(() => {
                    const allOpts = options ?? [];
                    const avecParcours = allOpts.filter((o) => o.parcours_nom);
                    const sansParcours = allOpts.filter((o) => !o.parcours_nom);
                    const groupes = new Map<string, typeof allOpts>();
                    avecParcours.forEach((o) => {
                      const key = o.parcours_nom!;
                      if (!groupes.has(key)) groupes.set(key, []);
                      groupes.get(key)!.push(o);
                    });

                    return (
                      <>
                        {Array.from(groupes.entries()).map(([parcNom, opts]) => (
                          <div key={parcNom}>
                            <p className="px-2 py-1 text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 rounded">
                              {parcNom}
                            </p>
                            {opts.map((o) => (
                              <label
                                key={o.id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                  copyMatieresOptionsCibles.includes(o.nom)
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={copyMatieresOptionsCibles.includes(o.nom)}
                                  onChange={() => toggleCopyMatiereCible(o.nom)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-gray-100">{o.nom}</span>
                                {copyMatieresOptionsCibles.includes(o.nom) && (
                                  <CheckCircle size={14} className="text-blue-600 ml-auto" />
                                )}
                              </label>
                            ))}
                          </div>
                        ))}
                        {sansParcours.length > 0 && (
                          <div>
                            {groupes.size > 0 && (
                              <p className="px-2 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded">
                                Autres
                              </p>
                            )}
                            {sansParcours.map((o) => (
                              <label
                                key={o.id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                  copyMatieresOptionsCibles.includes(o.nom)
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={copyMatieresOptionsCibles.includes(o.nom)}
                                  onChange={() => toggleCopyMatiereCible(o.nom)}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-900 dark:text-gray-100">{o.nom}</span>
                                {copyMatieresOptionsCibles.includes(o.nom) && (
                                  <CheckCircle size={14} className="text-blue-600 ml-auto" />
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowCopyMatieresModal(false)}>
                Annuler
              </Button>
              <Button
                variant="primary"
                icon={<Copy size={16} />}
                onClick={handleCopierMatieres}
                disabled={copierMatieresLoading || copyMatieresOptionsCibles.length === 0}
              >
                {copierMatieresLoading ? 'Copie en cours...' : `Copier vers ${copyMatieresOptionsCibles.length} option(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rattachement Matières Orphelines */}
      {showRattachModal && orphelinesData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Rattacher les matières orphelines
              </h3>
              <button onClick={() => setShowRattachModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sélectionnez les matières à rattacher puis choisissez l'option cible.
                Une UE sera créée automatiquement si nécessaire.
              </p>

              {/* Sélection de l'option cible */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Option cible <span className="text-red-500">*</span>
                </label>
                <select
                  value={rattachOption}
                  onChange={(e) => setRattachOption(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                >
                  <option value="">-- Sélectionner une option --</option>
                  {(options || []).filter(o => o.actif !== false).map(o => (
                    <option key={o.id} value={o.nom}>{o.code} — {o.nom}</option>
                  ))}
                </select>
              </div>

              {/* Liste des matières orphelines */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                <div className="sticky top-0 bg-gray-50 dark:bg-gray-700 px-3 py-2 flex items-center gap-2 border-b border-gray-200 dark:border-gray-600">
                  <input
                    type="checkbox"
                    checked={rattachSelectedIds.size === orphelinesData.matieres.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setRattachSelectedIds(new Set(orphelinesData.matieres.map(m => m.id)));
                      } else {
                        setRattachSelectedIds(new Set());
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                    {rattachSelectedIds.size}/{orphelinesData.matieres.length} sélectionnée(s)
                  </span>
                </div>
                {orphelinesData.matieres.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={rattachSelectedIds.has(m.id)}
                      onChange={(e) => {
                        const s = new Set(rattachSelectedIds);
                        e.target.checked ? s.add(m.id) : s.delete(m.id);
                        setRattachSelectedIds(s);
                      }}
                      className="rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.nom_matiere}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{m.code_matiere}</span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                      {m.niveau} S{m.semestre}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowRattachModal(false)}>Annuler</Button>
              <Button
                variant="primary"
                disabled={rattachLoading || !rattachOption || rattachSelectedIds.size === 0}
                onClick={async () => {
                  try {
                    const res = await rattacherMatieres({
                      matiere_ids: Array.from(rattachSelectedIds),
                      option_nom: rattachOption,
                    }).unwrap();
                    setMessage({ type: 'success', text: res.message });
                    setShowRattachModal(false);
                    refetchMatieres();
                    refetchOrphelines();
                  } catch (err: any) {
                    setMessage({ type: 'error', text: err?.data?.error || 'Erreur lors du rattachement' });
                  }
                }}
              >
                {rattachLoading ? 'Rattachement...' : `Rattacher ${rattachSelectedIds.size} matière(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nettoyage Orphelines */}
      {showNettoyerModal && nettoyerAnalyse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Nettoyage des matières orphelines
              </h3>
              <button onClick={() => setShowNettoyerModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{nettoyerAnalyse.total}</div>
                  <div className="text-xs text-gray-500">Total orphelines</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">{nettoyerAnalyse.nb_supprimables}</div>
                  <div className="text-xs text-green-600">Supprimables</div>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                  <div className="text-2xl font-bold text-orange-700 dark:text-orange-400">{nettoyerAnalyse.nb_avec_dependances}</div>
                  <div className="text-xs text-orange-600">Avec dépendances</div>
                </div>
              </div>

              {/* Supprimables */}
              {nettoyerAnalyse.nb_supprimables > 0 && (
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                    Seront supprimées ({nettoyerAnalyse.nb_supprimables}) :
                  </p>
                  <div className="border border-green-200 dark:border-green-800 rounded-lg max-h-40 overflow-y-auto">
                    {nettoyerAnalyse.supprimables.map((m: any) => (
                      <div key={m.id} className="flex items-center justify-between px-3 py-1.5 text-sm border-b border-green-100 dark:border-green-900 last:border-0">
                        <span className="text-gray-900 dark:text-gray-100">{m.nom}</span>
                        <span className="text-xs text-gray-500">{m.niveau} S{m.semestre}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Avec dépendances */}
              {nettoyerAnalyse.nb_avec_dependances > 0 && (
                <div>
                  <p className="text-sm font-semibold text-orange-700 dark:text-orange-400 mb-2">
                    Seront désactivées ({nettoyerAnalyse.nb_avec_dependances}) — ont des données liées :
                  </p>
                  <div className="border border-orange-200 dark:border-orange-800 rounded-lg max-h-40 overflow-y-auto">
                    {nettoyerAnalyse.avec_dependances.map((m: any) => (
                      <div key={m.id} className="flex items-center justify-between px-3 py-1.5 text-sm border-b border-orange-100 dark:border-orange-900 last:border-0">
                        <span className="text-gray-900 dark:text-gray-100">{m.nom}</span>
                        <div className="flex gap-1">
                          {Object.entries(m.dependances).map(([k, v]) => (
                            <span key={k} className="text-[10px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded">
                              {k}: {String(v)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Ces matières ne seront pas supprimées mais désactivées (actif=false) pour ne plus apparaître.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="outline" onClick={() => setShowNettoyerModal(false)}>Annuler</Button>
              <Button
                variant="danger"
                disabled={nettoyerLoading}
                onClick={async () => {
                  try {
                    const res = await nettoyerOrphelines({ mode: 'executer' }).unwrap();
                    setMessage({ type: 'success', text: res.message || 'Nettoyage terminé' });
                    setShowNettoyerModal(false);
                    setNettoyerAnalyse(null);
                    refetchMatieres();
                    refetchOrphelines();
                  } catch (err: any) {
                    setMessage({ type: 'error', text: err?.data?.error || 'Erreur lors du nettoyage' });
                  }
                }}
              >
                {nettoyerLoading ? 'Nettoyage...' : `Confirmer le nettoyage`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nombre de Sujets à Imprimer */}
      {showSujetsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Nombre de sujets à imprimer
              </h3>
              <button onClick={() => setShowSujetsModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Filtres */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Niveau</label>
                  <select
                    value={sujetsNiveau}
                    onChange={(e) => setSujetsNiveau(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  >
                    {NIVEAUX.map((n) => (
                      <option key={n.value} value={n.value}>{n.value}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Option</label>
                  <select
                    value={sujetsOption}
                    onChange={(e) => setSujetsOption(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  >
                    <option value="">Toutes les options</option>
                    {(options || []).filter(o => o.actif !== false).map(o => (
                      <option key={o.id} value={o.nom}>{o.code} — {o.nom}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Semestre</label>
                  <select
                    value={sujetsSemestre}
                    onChange={(e) => setSujetsSemestre(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                  >
                    <option value="">Tous</option>
                    {getSemestresForNiveau(sujetsNiveau).map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Résumé */}
              {sujetsData && (
                <div className="flex gap-4 text-sm">
                  <span className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg font-medium">
                    {sujetsData.nb_matieres} matière(s)
                  </span>
                  <span className="px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg font-medium">
                    {sujetsData.nb_etudiants_total} étudiant(s) actif(s)
                  </span>
                </div>
              )}

              {/* Tableau */}
              {sujetsFetching ? (
                <div className="flex justify-center py-8"><Spinner /></div>
              ) : sujetsData && sujetsData.matieres.length > 0 ? (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-gray-300">Code</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-gray-300">Matière</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 dark:text-gray-300">Option</th>
                        <th className="text-center px-3 py-2 font-semibold text-gray-700 dark:text-gray-300">S</th>
                        <th className="text-center px-3 py-2 font-semibold text-blue-700 dark:text-blue-400">Nb Sujets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sujetsData.matieres.map((m, i) => (
                        <tr key={m.matiere_id} className={i % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">{m.code_matiere}</td>
                          <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{m.nom_matiere}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                            {m.options_concernees.join(', ') || <span className="text-amber-500">Sans option</span>}
                          </td>
                          <td className="px-3 py-2 text-center text-xs text-gray-500">S{m.semestre}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="font-bold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">
                              {m.nb_sujets}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-100 dark:bg-gray-700 font-semibold">
                        <td colSpan={4} className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">Total sujets à imprimer :</td>
                        <td className="px-3 py-2 text-center text-blue-700 dark:text-blue-400">
                          {sujetsData.matieres.reduce((s, m) => s + m.nb_sujets, 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : sujetsData ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Aucune matière trouvée pour ces filtres.</p>
              ) : null}

              {/* Bouton imprimer */}
              {sujetsData && sujetsData.matieres.length > 0 && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    icon={<Printer size={16} />}
                    onClick={() => {
                      const w = window.open('', '_blank');
                      if (!w) return;
                      const rows = sujetsData.matieres.map((m, i) =>
                        `<tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
                          <td style="padding:6px 10px;border:1px solid #e5e7eb;font-family:monospace;font-size:11px">${m.code_matiere}</td>
                          <td style="padding:6px 10px;border:1px solid #e5e7eb">${m.nom_matiere}</td>
                          <td style="padding:6px 10px;border:1px solid #e5e7eb;font-size:11px">${m.options_concernees.join(', ')}</td>
                          <td style="padding:6px 10px;border:1px solid #e5e7eb;text-align:center">S${m.semestre}</td>
                          <td style="padding:6px 10px;border:1px solid #e5e7eb;text-align:center;font-weight:bold;color:#1d4ed8">${m.nb_sujets}</td>
                        </tr>`
                      ).join('');
                      w.document.write(`<html><head><title>Sujets à imprimer - ${sujetsNiveau} ${sujetsOption || 'Toutes options'}</title></head><body style="font-family:Arial;padding:20px">
                        <h2>Nombre de sujets à imprimer — ${sujetsNiveau} ${sujetsOption ? `— ${sujetsOption}` : ''}</h2>
                        <p>${sujetsData.nb_matieres} matière(s), ${sujetsData.nb_etudiants_total} étudiant(s)</p>
                        <table style="width:100%;border-collapse:collapse;margin-top:16px">
                          <thead><tr style="background:#1e3a5f;color:#fff">
                            <th style="padding:8px 10px;border:1px solid #e5e7eb;text-align:left">Code</th>
                            <th style="padding:8px 10px;border:1px solid #e5e7eb;text-align:left">Matière</th>
                            <th style="padding:8px 10px;border:1px solid #e5e7eb;text-align:left">Options</th>
                            <th style="padding:8px 10px;border:1px solid #e5e7eb;text-align:center">S</th>
                            <th style="padding:8px 10px;border:1px solid #e5e7eb;text-align:center">Nb Sujets</th>
                          </tr></thead>
                          <tbody>${rows}</tbody>
                          <tfoot><tr style="background:#e5e7eb;font-weight:bold">
                            <td colspan="4" style="padding:8px 10px;border:1px solid #e5e7eb;text-align:right">Total :</td>
                            <td style="padding:8px 10px;border:1px solid #e5e7eb;text-align:center;color:#1d4ed8">${sujetsData.matieres.reduce((s, m) => s + m.nb_sujets, 0)}</td>
                          </tr></tfoot>
                        </table>
                      </body></html>`);
                      w.document.close();
                      w.print();
                    }}
                  >
                    Imprimer
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal : Auto-créer les options CS depuis FI (basé sur les étudiants importés) */}
      <AutoCreerOptionsCSModal
        open={showAutoCSModal}
        onClose={() => setShowAutoCSModal(false)}
        onDone={(msg) => {
          setShowAutoCSModal(false);
          setMessage({ type: 'success', text: msg });
          refetchMatieres();
          refetchUEs();
        }}
      />
    </div>
  );
};

export default GestionMatieres;


// ─── Section Diagnostic : paires de classes similaires ─────────────────────
// Détecte les paires (niveau, option_A / option_B) partageant ≥90% de leurs
// matières par nom_matiere et propose un bouton pour copier les attributions
// formateur d'une classe vers l'autre en un clic.
interface PairesSimilairesSectionProps {
  anneeLibelle: string;
}

const PairesSimilairesSection: React.FC<PairesSimilairesSectionProps> = ({ anneeLibelle }) => {
  const { data, isLoading, refetch } = useGetPairesSimilairesQuery(
    { seuil: 0.9, annee_academique: anneeLibelle },
    { skip: !anneeLibelle },
  );
  const [copierAttributions, { isLoading: isCopying }] = useCopierAttributionsMutation();
  const [confirmPair, setConfirmPair] = useState<null | (typeof data extends { paires: Array<infer T> } ? T : any)>(null);
  const [result, setResult] = useState<null | { type: 'success' | 'error'; text: string }>(null);

  const paires = data?.paires || [];

  const handleCopier = async (paire: any, sens: 'a2b' | 'b2a') => {
    setResult(null);
    const source_option = sens === 'a2b' ? paire.source_option : paire.cible_option;
    const cible_option = sens === 'a2b' ? paire.cible_option : paire.source_option;
    try {
      const res = await copierAttributions({
        niveau: paire.niveau,
        source_option,
        cible_option,
        annee_academique: anneeLibelle,
      }).unwrap();
      setResult({ type: 'success', text: res.message });
      setConfirmPair(null);
      refetch();
    } catch (e: any) {
      setResult({ type: 'error', text: e?.data?.error || 'Erreur lors de la copie' });
    }
  };

  return (
    <Card>
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Copy size={20} className="text-blue-500" />
          Classes similaires (≥ 90 % de matières en commun)
          {paires.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-medium">
              {paires.length}
            </span>
          )}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Paires détectées automatiquement. Copiez les attributions formateur d'une classe vers l'autre en un clic.
        </p>
      </div>

      <div className="p-4">
        {result && (
          <div className={`mb-3 p-3 rounded-lg text-sm ${
            result.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {result.text}
          </div>
        )}

        {!anneeLibelle ? (
          <p className="text-sm text-gray-500 text-center py-6">Aucune année académique active.</p>
        ) : isLoading ? (
          <div className="flex justify-center py-8"><Spinner size="md" /></div>
        ) : paires.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <CheckCircle size={40} className="mx-auto mb-2 text-green-500" />
            <p>Aucune paire de classes suffisamment similaire pour l'année {anneeLibelle}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Niveau</th>
                  <th className="px-3 py-2 text-left font-semibold">Classe A (source)</th>
                  <th className="px-3 py-2 text-left font-semibold">Classe B (cible)</th>
                  <th className="px-3 py-2 text-center font-semibold">Communes / min</th>
                  <th className="px-3 py-2 text-center font-semibold">Taux</th>
                  <th className="px-3 py-2 text-center font-semibold">Formateurs A / B</th>
                  <th className="px-3 py-2 text-center font-semibold">À copier</th>
                  <th className="px-3 py-2 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {paires.map((p, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-3 py-2 font-mono text-xs">{p.niveau}</td>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{p.source_option}</td>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{p.cible_option}</td>
                    <td className="px-3 py-2 text-center tabular-nums text-gray-700 dark:text-gray-300">
                      {p.nb_communes} / {Math.min(p.nb_matieres_source, p.nb_matieres_cible)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        p.taux >= 1.0
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      }`}>
                        {Math.round(p.taux * 100)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center tabular-nums text-gray-700 dark:text-gray-300">
                      {p.nb_attributions_source} / {p.nb_attributions_cible}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.nb_copiables > 0 ? (
                        <span className="inline-block px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded font-semibold">
                          {p.nb_copiables}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setConfirmPair(p as any)}
                        disabled={p.nb_copiables === 0 || isCopying}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <Copy size={12} />
                        Copier A→B
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal confirmation */}
      {confirmPair && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Confirmer la copie</h3>
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p>
                  Copier <strong>{(confirmPair as any).nb_copiables}</strong> attribution(s) formateur depuis :
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2 font-mono text-xs">
                  <div><strong>Source</strong> : {(confirmPair as any).niveau} · {(confirmPair as any).source_option}</div>
                  <div><strong>Cible</strong> : {(confirmPair as any).niveau} · {(confirmPair as any).cible_option}</div>
                </div>
                <p className="text-xs text-gray-500">
                  Seules les matières présentes des deux côtés (correspondance exacte du nom) sont copiées. Les attributions déjà en place en cible ne sont pas modifiées.
                </p>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setConfirmPair(null)} disabled={isCopying}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={isCopying ? <RefreshCw size={14} className="animate-spin" /> : <Copy size={14} />}
                  onClick={() => handleCopier(confirmPair, 'a2b')}
                  disabled={isCopying}
                >
                  Confirmer la copie
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
